import React, { useEffect, useState } from "react";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import { Input, Button, InputSelect } from "../../../../form";
import ProfessionMapSelection from "./classProfessionMapSelection";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import ClassStudentComponents, { ClassSudentComponentsPicker } from "./classStudentComponents";
import ToggleComponent from "../../../../common/toggleComponent";
import RadioGroupInput from "../../../../common/radioGroupInput";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon
} from "@heroicons/react/24/outline";
import { BaseModal } from "../../../../popups";
import { ColorPicker, ImageUploader } from "../../../../common";
import { convertRGBAObjectToString, uploadFileIfNeeded } from "../../../../../utils";
import { RxExit } from "react-icons/rx";
import { getRegionalEvents } from "../../../../../service";
import ClipLoader from "react-spinners/ClipLoader";
import { toast } from "react-toastify";

const defaulTStudentAttributesToMatch = [
  {
    id: "professions",
    value: "professions"
  },
  {
    id: "regionalEvent",
    value: "regionalEvent"
  }
];

const VipClassDemoTanIdsEditor = ({
  variant,
  tanIds,
  addVipClassDemoTanId,
  removeVipClassDemoTanId
}) => {
  const [value, setValue] = useState("");
  const list = Array.isArray(tanIds) ? tanIds : [];

  const handleSubmit = (e) => {
    e.preventDefault();
    addVipClassDemoTanId(variant, value);
    setValue("");
  };

  return (
    <div className="px-4 py-3 mb-2 bg-white border border-gray-200 rounded-lg">
      <p className="font-semibold text-gray-800 mb-2">Class demo Tan IDs</p>
      <form onSubmit={handleSubmit} className="grid grid-cols-12 gap-4 items-end">
        <Input
          className={"col-span-8 !mb-0"}
          inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
          labelClassName={"text-sm font-normal text-[#606060]"}
          label="Tan ID"
          placeholder="Enter tan id…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addVipClassDemoTanId(variant, value);
              setValue("");
            }
          }}
        />
        <button type="submit" className="bg-[#515151] text-white rounded-full px-4 py-3 col-span-4">
          Add
        </button>
      </form>
      {list.length > 0 && (
        <ul className="space-y-1 text-sm mt-2">
          {list.map((id, idx) => (
            <li
              key={`tan-${idx}-${id}`}
              className="flex items-center justify-between gap-2 py-1 px-2 bg-gray-50 rounded">
              <span className="font-mono break-all">{id}</span>
              <button
                type="button"
                onClick={() => removeVipClassDemoTanId(variant, idx)}
                className="text-red-600 shrink-0 p-1"
                aria-label="Remove tan id">
                <TrashIcon className="w-5 h-5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ClassProjectSetup = ({
  org,
  currentService,
  serverBaseUrl,
  externalProjectType,
  region = "default",
  classProjectTypes,
  setClassProjectTypes,
  regions,
  professionMaps,
  systemAdminGroups,
  professionTypes,
  emailTemplates,
  toggleSendEmailAfterCompletion,
  setEmailTemplateId,
  regionalProjectTypes,
  displayToggle,
  setDisplayToggle,
  updateClassProjectTitle,
  duplicateClassProjectTypeForRegion,
  addClassStudentComponent,
  deleteClassStudentComponent,
  onDragEndClassStudentSetup,
  updateBackgroundHeading,
  updateForegroundHeading,
  updateSubText,
  updateButtonText,
  updateDownloadType,
  updateHTMLBox,
  updateVideosPlayerTitle,
  updateVideosPlayerUrl,
  addNewVideoInVideosPlayer,
  deleteVideoInVideosPlayer,
  toggleVideoPlayerAptitudeCloud,
  selectProjectTypeRegionalLink,
  selectUsersGroup,
  discoverChampionCustomUrl,
  selectProfessionType,
  toggleUseOrganizationStrengthsListInAptitudeCloud,
  toggleUseOrganizationRolesListInAptitudeCloud,
  toggleUseOrganizationProfessionsListInAptitudeCloud,
  toggleShowAsListInAptitudeCloud,
  updateAptitudeCloudOption,
  addAptitudeCloudOption,
  deleteAptitudeCloudOption,
  addCustomVideoOption,
  deleteCustomVideoOption,
  updateCustomVideoOption,
  saveTeacherAdminSetup,
  deleteRegionalClassProjectType,
  deleteClassProject,
  duplicateClassProject,
  toggleClassProjectTypeEnable,
  toggleProjectLoginButton,
  setExternalProjectId,
  toggleShowClassStudentComponentInPdf,
  setOrderOfClassProject,
  updateListLength,
  toggleReplacePreviousSelections,
  addTableColumn,
  updateTableColumn,
  deleteTableColumn,
  addAttitudeListItem,
  updateAttitudeListItem,
  deleteAttitudeListItem,
  updateAttitudeComponentField,
  updateStudentRandomInputsNumber,
  updateStudentRandomInputsField,
  addStudentRandomInputsOption,
  removeStudentRandomInputsOption,
  updateStudentRandomInputsOption,
  updateMapImage,
  setCustomVIPProductId,
  setVIPTestUrlPath,
  setVIPConfigProfileSetupType,
  toggleParentsCustomVipTest,
  toggleVipConfigPage,
  addVipConfigStepPage,
  storeVipConfigThemeInProjectType,
  addVipConfigPageComponent,
  deleteVipConfigPageComponent,
  onDragEndVipConfigPageComponent,
  updateKeyValueInVipConfigPageComponent,
  removeVipConfigStepPage,
  setVIPServerUrl,
  updateTotalBadgesToShow,
  selectStudentAttributesToMatch,
  regionalEvents,
  setRegionalEventProfileType,
  selectRegionalEvent,
  updateSimilarProfileBadgesNavigationPopupField,
  updateSubInputField,
  updateProjectFor,
  setProjectGroup,
  updateProjectResources,
  languages,
  toggleVipClassPageEnabled,
  addVipClassDemoTanId,
  removeVipClassDemoTanId,
  updateInfoPopupField
}) => {
  const [vipThemeModalType, setVipThemeModalType] = useState(null);
  const [vipThemeModalSavedValues, setVipThemeModalSavedValues] = useState(null);
  const [showElement, setShowElement] = useState({});

  const openVipConfigThemeModal = (
    type = "themeSetup",
    savedValues = null,
    variant,
    region,
    stepPageIndex = null,
    configKey = "vipConfig"
  ) => {
    if (type === "themeSetup") {
      setVipThemeModalType(type);
      setVipThemeModalSavedValues({ ...(savedValues || {}), variant, region, configKey });
    }
  };

  const storeVipConfigTheme = async (savedValues, uploadProgress, setUploadProgress) => {
    const temp = { ...savedValues?.logos };
    const keys = Object.keys(temp);
    for (const key of keys) {
      const children = temp[key];
      if (typeof children === "string") {
        temp[key] = await uploadFileIfNeeded(children, "vip-test-logos", (progress) => {
          setUploadProgress({ ...uploadProgress, [key]: progress?.progress });
        });
        setUploadProgress({ ...uploadProgress, [key]: 0 });
      } else {
        const childKeys = Object.keys(temp[key]);
        for (const childKey of childKeys) {
          const image = temp[key][childKey];
          if (image) {
            temp[key][childKey] = await uploadFileIfNeeded(image, "vip-test-logos", (progress) => {
              setUploadProgress({ ...uploadProgress, [`${key}-${childKey}`]: progress?.progress });
            });
            setUploadProgress({ ...uploadProgress, [`${key}-${childKey}`]: 0 });
          }
        }
      }
    }
    storeVipConfigThemeInProjectType(
      savedValues?.variant,
      savedValues?.region,
      {
        ...savedValues,
        logos: temp
      },
      externalProjectType,
      savedValues?.configKey || "vipConfig"
    );
    setVipThemeModalType(null);
  };

  const tabRegion = externalProjectType ? null : regions.find((r) => r._id === region);
  const tabRegionName = externalProjectType
    ? "External"
    : region === "default"
      ? "Default"
      : tabRegion?.name_en || tabRegion?.name_de || tabRegion?.name_fr || tabRegion?.name_it;

  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-white">
      <p
        className="font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => {
          setDisplayToggle({
            ...displayToggle,
            [`projectTypes-${region}`]: !displayToggle?.[`projectTypes-${region}`]
          });
        }}>
        ({tabRegionName ? tabRegionName : "Default"}) Class Project Type/s
      </p>

      {displayToggle?.[`projectTypes-${region}`] && region !== "default" && (
        <div className="my-4">
          <label className="block font-bold mb-1" htmlFor={region}>
            Region-specific
          </label>
          <InputSelect
            name={region}
            defaultValue={{ value: region, label: tabRegionName }}
            options={regions
              .map((region) => {
                if (region._id === region || Object.keys(regionalProjectTypes).includes(region._id))
                  return null;
                return {
                  value: region._id,
                  label: region.name_en || region.name_de || region.name_fr || region.name_it
                };
              })
              .filter((value) => value !== null)}
            onChange={(e) => {
              duplicateClassProjectTypeForRegion(classProjectTypes, e.value, region);
            }}
          />
        </div>
      )}

      {displayToggle?.[`projectTypes-${region}`] &&
        Object.keys(classProjectTypes).map((variant, index) => {
          const variantDetails = classProjectTypes[variant];
          const variantTitle =
            variantDetails?.title?.en ||
            variantDetails?.title?.fr ||
            variantDetails?.title?.de ||
            variantDetails?.title?.it;

          const hasTeamProfilePageEnabled =
            !externalProjectType &&
            variantDetails?.classPage?.some((component) => component.id === "teamProfilePage");

          const toggleId = externalProjectType
            ? `externalProjectType-${variant}`
            : `classProjectType-${region}-${variant}`;

          return (
            <div key={index} className="my-10 border-[1px] border-[#606060] rounded-md p-4">
              <div className="flex items-center justify-between">
                <p
                  className="w-full text-lg font-bold mb-2 cursor-pointer select-none"
                  onClick={() => {
                    setDisplayToggle({
                      ...displayToggle,
                      [toggleId]: !displayToggle?.[toggleId]
                    });
                  }}>
                  {variantTitle}
                </p>
                <ToggleComponent
                  handleCompToggle={() =>
                    toggleClassProjectTypeEnable(variant, region, externalProjectType)
                  }
                  isToggle={variantDetails?.enabled}
                />
              </div>

              {!externalProjectType && (
                <div className="flex items-end justify-end">
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-[#606060]"}
                    label={"List Order"}
                    placeholder={"0"}
                    value={variantDetails?.order}
                    onChange={(e) => {
                      setOrderOfClassProject(variant, region, e.target.value);
                    }}
                  />
                </div>
              )}

              {externalProjectType && (
                <div className="flex items-end justify-end">
                  <Input
                    className={"w-full"}
                    inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                    labelClassName={"text-sm font-normal text-[#606060]"}
                    label={"Project URL"}
                    placeholder={"pays-de-la-loire"}
                    fixedPrefix={`${
                      process.env.REACT_APP_MF_SITE_DOMAIN || "https://www.mission-future.com"
                    }/${org.translationPrefix}/${
                      currentService?.type?.split("mission-future-")[1]
                    }/vip/`} // edit here
                    value={variantDetails?.projectId}
                    onChange={(e) => {
                      setExternalProjectId(variant, e.target.value);
                    }}
                    copyable={true}
                  />
                </div>
              )}

              <div className={`${displayToggle?.[toggleId] ? "block" : "hidden"}`}>
                {/* Variant titles */}
                <div className="flex flex-row gap-1 my-2">
                  {["en", "de", "fr", "it"].map((lang) => {
                    return (
                      <Input
                        key={lang}
                        className={"w-full"}
                        inputClassName={"rounded-full border-none bg-[#F6F5F5]"}
                        labelClassName={"text-sm font-normal text-[#606060]"}
                        label={lang.toUpperCase() + " Title"}
                        placeholder={lang}
                        value={variantDetails?.title?.[lang]}
                        onChange={(e) => {
                          updateClassProjectTitle(
                            variant,
                            {
                              ...variantDetails.title,
                              [lang]: e.target.value
                            },
                            region,
                            externalProjectType
                          );
                        }}
                      />
                    );
                  })}
                </div>

                <div className="mb-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="font-bold text-lg mb-3">Login Buttons</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p>Enable Class Login Button</p>
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleProjectLoginButton(
                            variant,
                            region,
                            externalProjectType,
                            "enableClassLoginButton"
                          )
                        }
                        isToggle={!!variantDetails?.enableClassLoginButton}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <p>Enable Student Login Button</p>
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleProjectLoginButton(
                            variant,
                            region,
                            externalProjectType,
                            "enableStudentLoginButton"
                          )
                        }
                        isToggle={!!variantDetails?.enableStudentLoginButton}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <p>Enable Parent Login Button</p>
                      <ToggleComponent
                        handleCompToggle={() =>
                          toggleProjectLoginButton(
                            variant,
                            region,
                            externalProjectType,
                            "enableParentLoginButton"
                          )
                        }
                        isToggle={!!variantDetails?.enableParentLoginButton}
                      />
                    </div>
                  </div>
                </div>

                {/* Checkbox for Project Type  */}
                {externalProjectType && (
                  <div className="flex flex-col items-start justify-center">
                    <p className="font-bold text-lg mb-2 cursor-pointer select-none">Project For</p>
                    <RadioGroupInput
                      srLabel="Project For"
                      value={variantDetails?.projectFor}
                      preselected={variantDetails?.projectFor}
                      options={[
                        {
                          id: "class",
                          name: "Class",
                          description: "this will redirect to the class app"
                        },
                        {
                          id: "champions",
                          name: "Champions",
                          description: "this will redirect to the champions app"
                        }
                      ]}
                      onSelect={(id) => {
                        updateProjectFor(variant, index, id, region, externalProjectType);
                      }}
                    />
                  </div>
                )}

                {!externalProjectType && (
                  <>
                    {/* Select profession map */}
                    <ProfessionMapSelection
                      professionMaps={professionMaps}
                      classProjectTypes={classProjectTypes}
                      setClassProjectTypes={setClassProjectTypes}
                      variant={variant}
                      variantDetails={variantDetails}
                      type={"classOverallProfessionMap"}
                    />
                    <ProfessionMapSelection
                      professionMaps={professionMaps}
                      classProjectTypes={classProjectTypes}
                      setClassProjectTypes={setClassProjectTypes}
                      variant={variant}
                      variantDetails={variantDetails}
                      type={"classGroupedProfessionMap"}
                    />
                    <ProfessionMapSelection
                      professionMaps={professionMaps}
                      classProjectTypes={classProjectTypes}
                      setClassProjectTypes={setClassProjectTypes}
                      variant={variant}
                      variantDetails={variantDetails}
                      type={"studentProfessionMap"}
                    />
                  </>
                )}

                <CustomVIPConfig
                  showElement={showElement}
                  setShowElement={setShowElement}
                  title="VIP Configuration"
                  configKey="vipConfig"
                  org={org}
                  serverBaseUrl={serverBaseUrl}
                  externalProjectType={externalProjectType}
                  variantDetails={variantDetails}
                  variant={variant}
                  region={region}
                  emailTemplates={emailTemplates}
                  toggleSendEmailAfterCompletion={toggleSendEmailAfterCompletion}
                  setEmailTemplateId={setEmailTemplateId}
                  setCustomVIPProductId={setCustomVIPProductId}
                  setVIPTestUrlPath={setVIPTestUrlPath}
                  setVIPConfigProfileSetupType={setVIPConfigProfileSetupType}
                  toggleParentsCustomVipTest={toggleParentsCustomVipTest}
                  toggleVipConfigPage={toggleVipConfigPage}
                  addVipConfigStepPage={addVipConfigStepPage}
                  removeVipConfigStepPage={removeVipConfigStepPage}
                  addVipConfigPageComponent={addVipConfigPageComponent}
                  deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                  onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                  updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
                  openVipConfigThemeModal={openVipConfigThemeModal}
                  setVIPServerUrl={setVIPServerUrl}
                  systemAdminGroups={systemAdminGroups}
                  setProjectGroup={setProjectGroup}
                />

                {variantDetails?.vipConfig?.parentsCustomVipTest && (
                  <CustomVIPConfig
                    showElement={showElement}
                    setShowElement={setShowElement}
                    title="Parent VIP Configuration"
                    configKey="parentVipConfig"
                    org={org}
                    serverBaseUrl={serverBaseUrl}
                    externalProjectType={externalProjectType}
                    variantDetails={variantDetails}
                    variant={variant}
                    region={region}
                    emailTemplates={emailTemplates}
                    toggleSendEmailAfterCompletion={toggleSendEmailAfterCompletion}
                    setEmailTemplateId={setEmailTemplateId}
                    setCustomVIPProductId={setCustomVIPProductId}
                    setVIPTestUrlPath={setVIPTestUrlPath}
                    setVIPConfigProfileSetupType={setVIPConfigProfileSetupType}
                    toggleParentsCustomVipTest={toggleParentsCustomVipTest}
                    toggleVipConfigPage={toggleVipConfigPage}
                    addVipConfigStepPage={addVipConfigStepPage}
                    removeVipConfigStepPage={removeVipConfigStepPage}
                    addVipConfigPageComponent={addVipConfigPageComponent}
                    deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                    onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                    updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
                    openVipConfigThemeModal={openVipConfigThemeModal}
                    setVIPServerUrl={setVIPServerUrl}
                    systemAdminGroups={systemAdminGroups}
                    setProjectGroup={setProjectGroup}
                    showParentsCustomVipToggle={false}
                    showProductIdInput={false}
                  />
                )}

                {/* Project Resources */}
                {!externalProjectType && (
                  <ProjectResources
                    externalProjectType={externalProjectType}
                    variantDetails={variantDetails}
                    variant={variant}
                    region={region}
                    updateProjectResources={updateProjectResources}
                    showElement={showElement}
                    setShowElement={setShowElement}
                    languages={languages}
                  />
                )}

                {/* List Class page components */}
                {externalProjectType && (
                  <div className="flex items-center justify-between px-4 py-2 mb-2 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="font-semibold text-gray-800">
                      Enable class webpage for this VIP test
                    </span>
                    <ToggleComponent
                      handleCompToggle={() => toggleVipClassPageEnabled(variant)}
                      isToggle={variantDetails?.vipClassPageEnabled}
                    />
                  </div>
                )}
                {externalProjectType && variantDetails?.vipClassPageEnabled && (
                  <VipClassDemoTanIdsEditor
                    variant={variant}
                    tanIds={variantDetails?.vipClassDemoTanIds}
                    addVipClassDemoTanId={addVipClassDemoTanId}
                    removeVipClassDemoTanId={removeVipClassDemoTanId}
                  />
                )}

                {(!externalProjectType || variantDetails?.vipClassPageEnabled) && (
                  <div className="px-4 py-3 bg-gray-100 rounded-lg">
                    <p
                      className="w-full text-lg font-bold my-2 cursor-pointer select-none"
                      onClick={() => {
                        setDisplayToggle({
                          ...displayToggle,
                          [`${variant}-${region}`]: {
                            classPage: !displayToggle?.[`${variant}-${region}`]?.classPage,
                            studentPage: displayToggle?.[`${variant}-${region}`]?.studentPage,
                            parentPage: displayToggle?.[`${variant}-${region}`]?.parentPage,
                            teamProfilePage:
                              displayToggle?.[`${variant}-${region}`]?.teamProfilePage
                          }
                        });
                      }}>
                      Class Webpage UI
                    </p>
                    <div
                      className={`${
                        displayToggle?.[`${variant}-${region}`]?.classPage ? "block" : "hidden"
                      }`}>
                      <DragDropContext
                        onDragEnd={(result) => {
                          onDragEndClassStudentSetup(
                            "classPage",
                            variant,
                            result,
                            region,
                            externalProjectType
                          );
                        }}>
                        <Droppable droppableId={`classPage-${variant}-${region}-components`}>
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              {variantDetails?.classPage?.map(
                                (classPageElement, classPageElementIndex) => (
                                  <Draggable
                                    key={`classPage-${variant}-${classPageElement.id}-${classPageElementIndex}`}
                                    draggableId={`classPage-${variant}-${region}-${classPageElement.id}-${classPageElementIndex}`}
                                    index={classPageElementIndex}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        id={`classPage-${variant}-${region}-${classPageElement.id}-${classPageElementIndex}`}
                                        className="my-5">
                                        <ClassStudentComponents
                                          index={classPageElementIndex}
                                          region={region}
                                          externalProjectType={externalProjectType}
                                          type={"classPage"}
                                          projectVariant={variant}
                                          projectVariantTitle={variantTitle}
                                          componentType={classPageElement.id}
                                          backgroundHeading={classPageElement.backgroundHeading}
                                          updateBackgroundHeading={updateBackgroundHeading}
                                          foregroundHeading={classPageElement.foregroundHeading}
                                          updateForegroundHeading={updateForegroundHeading}
                                          subText={classPageElement.subText}
                                          updateSubText={updateSubText}
                                          buttonText={classPageElement.buttonText}
                                          updateButtonText={updateButtonText}
                                          html={classPageElement.html}
                                          updateHTMLBox={updateHTMLBox}
                                          videos={classPageElement.videos}
                                          updateVideosPlayerTitle={updateVideosPlayerTitle}
                                          updateVideosPlayerUrl={updateVideosPlayerUrl}
                                          addNewVideoInVideosPlayer={addNewVideoInVideosPlayer}
                                          deleteVideoInVideosPlayer={deleteVideoInVideosPlayer}
                                          videPlayerAptitudeCloudEnabled={
                                            classPageElement?.enableAttitudeCloud
                                          }
                                          toggleVideoPlayerAptitudeCloud={
                                            toggleVideoPlayerAptitudeCloud
                                          }
                                          regions={regions}
                                          selectedRegionalLinks={
                                            classPageElement?.selectedRegions || []
                                          }
                                          selectProjectTypeRegionalLink={
                                            selectProjectTypeRegionalLink
                                          }
                                          availableUsersGroups={systemAdminGroups}
                                          selectedUsersGroups={
                                            classPageElement?.selectedUsersGroups
                                          }
                                          selectUsersGroup={selectUsersGroup}
                                          discoverChampionCustomUrlValue={
                                            classPageElement?.discoverChampionCustomUrl
                                          }
                                          discoverChampionCustomUrl={discoverChampionCustomUrl}
                                          professionTypes={professionTypes}
                                          selectedProfessionType={classPageElement?.professionType}
                                          selectProfessionType={selectProfessionType}
                                          useOrganizationStrengthsListInAptitudeCloud={
                                            classPageElement?.useOrganizationStrengthsList
                                          }
                                          toggleUseOrganizationStrengthsListInAptitudeCloud={
                                            toggleUseOrganizationStrengthsListInAptitudeCloud
                                          }
                                          useOrganizationRolesListInAptitudeCloud={
                                            classPageElement?.useOrganizationRolesList
                                          }
                                          toggleUseOrganizationRolesListInAptitudeCloud={
                                            toggleUseOrganizationRolesListInAptitudeCloud
                                          }
                                          useOrganizationProfessionsListInAptitudeCloud={
                                            classPageElement?.useOrganizationProfessionsList
                                          }
                                          toggleUseOrganizationProfessionsListInAptitudeCloud={
                                            toggleUseOrganizationProfessionsListInAptitudeCloud
                                          }
                                          aptitudeCloudOptions={
                                            classPageElement?.aptitudeCloudOptions
                                          }
                                          showAsList={classPageElement?.showAsList}
                                          toggleShowAsListInAptitudeCloud={
                                            toggleShowAsListInAptitudeCloud
                                          }
                                          updateAptitudeCloudOption={updateAptitudeCloudOption}
                                          addAptitudeCloudOption={addAptitudeCloudOption}
                                          deleteAptitudeCloudOption={deleteAptitudeCloudOption}
                                          customVideoOptions={classPageElement?.customVideoOptions}
                                          addCustomVideoOption={addCustomVideoOption}
                                          deleteCustomVideoOption={deleteCustomVideoOption}
                                          updateCustomVideoOption={updateCustomVideoOption}
                                          removeComponent={deleteClassStudentComponent}
                                          updateListLength={updateListLength}
                                          replacePreviousSelections={
                                            classPageElement?.replacePreviousSelections
                                          }
                                          toggleReplacePreviousSelections={
                                            toggleReplacePreviousSelections
                                          }
                                          listLength={classPageElement?.listLength}
                                          columns={classPageElement?.columns}
                                          addTableColumn={addTableColumn}
                                          updateTableColumn={updateTableColumn}
                                          deleteTableColumn={deleteTableColumn}
                                          updateStudentRandomInputsNumber={
                                            updateStudentRandomInputsNumber
                                          }
                                          studentRandomInputsNumber={
                                            classPageElement?.numberOfInputs
                                          }
                                          studentRandomInputsPopupHeading={
                                            classPageElement?.popupHeading
                                          }
                                          studentRandomInputsPopupSubmitButtonText={
                                            classPageElement?.popupSubmitButtonText
                                          }
                                          studentRandomInputsPopupInputPlaceholder={
                                            classPageElement?.popupInputPlaceholder
                                          }
                                          studentRandomInputsWordCloudId={
                                            classPageElement?.studentRandomInputsWordCloudId
                                          }
                                          updateStudentRandomInputsField={
                                            updateStudentRandomInputsField
                                          }
                                          studentRandomInputsOptions={classPageElement?.options}
                                          addStudentRandomInputsOption={
                                            addStudentRandomInputsOption
                                          }
                                          removeStudentRandomInputsOption={
                                            removeStudentRandomInputsOption
                                          }
                                          updateStudentRandomInputsOption={
                                            updateStudentRandomInputsOption
                                          }
                                          totalBadgesToShow={classPageElement?.totalBadgesToShow}
                                          updateTotalBadgesToShow={updateTotalBadgesToShow}
                                          studentAttributesToMatch={
                                            classPageElement?.studentAttributesToMatch
                                          }
                                          selectStudentAttributesToMatch={
                                            selectStudentAttributesToMatch
                                          }
                                          defaultStudentAttributesToMatch={
                                            defaulTStudentAttributesToMatch
                                          }
                                          similarProfileBadgesNavigationPopup={
                                            classPageElement?.navigationPopup
                                          }
                                          updateSimilarProfileBadgesNavigationPopupField={
                                            updateSimilarProfileBadgesNavigationPopupField
                                          }
                                          updateMapImage={updateMapImage}
                                          mapImages={{
                                            sf: classPageElement?.sf_img,
                                            st: classPageElement?.st_img,
                                            nf: classPageElement?.nf_img,
                                            nt: classPageElement?.nt_img,
                                            icons: {
                                              sf: classPageElement?.sf_icons_img,
                                              st: classPageElement?.st_icons_img,
                                              nf: classPageElement?.nf_icons_img,
                                              nt: classPageElement?.nt_icons_img
                                            }
                                          }}
                                          subInputField={classPageElement?.subInputField}
                                          updateSubInputField={updateSubInputField}
                                          infoPopupHeading={classPageElement?.heading}
                                          infoPopupSubHeading={classPageElement?.subHeading}
                                          infoPopupHeadingColor={classPageElement?.headingColor}
                                          infoPopupSubHeadingColor={
                                            classPageElement?.subHeadingColor
                                          }
                                          infoPopupBackgroundColor={
                                            classPageElement?.backgroundColor
                                          }
                                          infoPopupCloseIconBackgroundColor={
                                            classPageElement?.closeIconBackgroundColor
                                          }
                                          infoPopupCloseIconColor={classPageElement?.closeIconColor}
                                          infoPopupButtonBackgroundColor={
                                            classPageElement?.buttonBackgroundColor
                                          }
                                          infoPopupButtonTextColor={
                                            classPageElement?.buttonTextColor
                                          }
                                          infoPopupAutoShow={classPageElement?.autoShowPopup}
                                          updateInfoPopupField={updateInfoPopupField}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                      <ClassSudentComponentsPicker
                        type={"classPage"}
                        projectVariant={variant}
                        addComponent={(type, projectVariant, value) =>
                          addClassStudentComponent(
                            type,
                            projectVariant,
                            value,
                            region,
                            externalProjectType
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* List Student page components */}
                <div className="px-4 py-3 bg-gray-100 rounded-lg mt-5">
                  <p
                    className="w-full text-lg font-bold my-2 cursor-pointer"
                    onClick={() => {
                      setDisplayToggle({
                        ...displayToggle,
                        [`${variant}-${region}`]: {
                          classPage: displayToggle?.[`${variant}-${region}`]?.classPage,
                          studentPage: !displayToggle?.[`${variant}-${region}`]?.studentPage,
                          parentPage: displayToggle?.[`${variant}-${region}`]?.parentPage,
                          teamProfilePage: displayToggle?.[`${variant}-${region}`]?.teamProfilePage
                        }
                      });
                    }}>
                    Student Webpage UI
                  </p>
                  <div
                    className={`${
                      displayToggle?.[`${variant}-${region}`]?.studentPage ? "block" : "hidden"
                    }`}>
                    <DragDropContext
                      onDragEnd={(result) => {
                        onDragEndClassStudentSetup(
                          "studentPage",
                          variant,
                          result,
                          region,
                          externalProjectType
                        );
                      }}>
                      <Droppable droppableId={`studentPage-${region}-${variant}-components`}>
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {variantDetails?.studentPage?.map(
                              (studentPageElement, studentPageElementIndex) => (
                                <Draggable
                                  key={`studentPage-${variant}-${studentPageElement.id}-${studentPageElementIndex}`}
                                  draggableId={`studentPage-${region}-${variant}-${studentPageElement.id}-${studentPageElementIndex}`}
                                  index={studentPageElementIndex}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      id={`studentPage-${region}-${variant}-${studentPageElement.id}-${studentPageElementIndex}`}
                                      className="my-5">
                                      <ClassStudentComponents
                                        index={studentPageElementIndex}
                                        region={region}
                                        externalProjectType={externalProjectType}
                                        type={"studentPage"}
                                        projectVariant={variant}
                                        projectVariantTitle={variantTitle}
                                        componentType={studentPageElement.id}
                                        backgroundHeading={studentPageElement.backgroundHeading}
                                        updateBackgroundHeading={updateBackgroundHeading}
                                        foregroundHeading={studentPageElement.foregroundHeading}
                                        updateForegroundHeading={updateForegroundHeading}
                                        subText={studentPageElement.subText}
                                        updateSubText={updateSubText}
                                        buttonText={studentPageElement.buttonText}
                                        updateButtonText={updateButtonText}
                                        downloadType={studentPageElement.downloadType}
                                        updateDownloadType={updateDownloadType}
                                        html={studentPageElement.html}
                                        updateHTMLBox={updateHTMLBox}
                                        videos={studentPageElement.videos}
                                        updateVideosPlayerTitle={updateVideosPlayerTitle}
                                        updateVideosPlayerUrl={updateVideosPlayerUrl}
                                        addNewVideoInVideosPlayer={addNewVideoInVideosPlayer}
                                        deleteVideoInVideosPlayer={deleteVideoInVideosPlayer}
                                        videPlayerAptitudeCloudEnabled={
                                          studentPageElement?.enableAttitudeCloud
                                        }
                                        toggleVideoPlayerAptitudeCloud={
                                          toggleVideoPlayerAptitudeCloud
                                        }
                                        regions={regions}
                                        selectedRegionalLinks={
                                          studentPageElement?.selectedRegions || []
                                        }
                                        selectProjectTypeRegionalLink={
                                          selectProjectTypeRegionalLink
                                        }
                                        availableUsersGroups={systemAdminGroups}
                                        selectedUsersGroups={
                                          studentPageElement?.selectedUsersGroups
                                        }
                                        discoverChampionCustomUrlValue={
                                          studentPageElement?.discoverChampionCustomUrl
                                        }
                                        selectUsersGroup={selectUsersGroup}
                                        discoverChampionCustomUrl={discoverChampionCustomUrl}
                                        professionTypes={professionTypes}
                                        selectedProfessionType={studentPageElement?.professionType}
                                        selectProfessionType={selectProfessionType}
                                        removeComponent={deleteClassStudentComponent}
                                        hideInPdf={studentPageElement?.hideInPdf}
                                        toggleShowClassStudentComponentInPdf={
                                          toggleShowClassStudentComponentInPdf
                                        }
                                        updateListLength={updateListLength}
                                        listLength={studentPageElement?.listLength}
                                        replacePreviousSelections={
                                          studentPageElement?.replacePreviousSelections
                                        }
                                        toggleReplacePreviousSelections={
                                          toggleReplacePreviousSelections
                                        }
                                        columns={studentPageElement?.columns}
                                        addTableColumn={addTableColumn}
                                        updateTableColumn={updateTableColumn}
                                        deleteTableColumn={deleteTableColumn}
                                        addAptitudeCloudOption={addAptitudeCloudOption}
                                        deleteAptitudeCloudOption={deleteAptitudeCloudOption}
                                        updateAptitudeCloudOption={updateAptitudeCloudOption}
                                        aptitudeCloudOptions={
                                          studentPageElement?.aptitudeCloudOptions
                                        }
                                        updateStudentRandomInputsNumber={
                                          updateStudentRandomInputsNumber
                                        }
                                        studentRandomInputsNumber={
                                          studentPageElement?.numberOfInputs
                                        }
                                        studentRandomInputsPopupHeading={
                                          studentPageElement?.popupHeading
                                        }
                                        studentRandomInputsPopupSubmitButtonText={
                                          studentPageElement?.popupSubmitButtonText
                                        }
                                        studentRandomInputsPopupInputPlaceholder={
                                          studentPageElement?.popupInputPlaceholder
                                        }
                                        studentRandomInputsWordCloudId={
                                          studentPageElement?.studentRandomInputsWordCloudId
                                        }
                                        updateStudentRandomInputsField={
                                          updateStudentRandomInputsField
                                        }
                                        studentRandomInputsOptions={studentPageElement?.options}
                                        addStudentRandomInputsOption={addStudentRandomInputsOption}
                                        removeStudentRandomInputsOption={
                                          removeStudentRandomInputsOption
                                        }
                                        updateStudentRandomInputsOption={
                                          updateStudentRandomInputsOption
                                        }
                                        totalBadgesToShow={studentPageElement?.totalBadgesToShow}
                                        updateTotalBadgesToShow={updateTotalBadgesToShow}
                                        studentAttributesToMatch={
                                          studentPageElement?.studentAttributesToMatch
                                        }
                                        selectStudentAttributesToMatch={
                                          selectStudentAttributesToMatch
                                        }
                                        defaultStudentAttributesToMatch={
                                          defaulTStudentAttributesToMatch
                                        }
                                        regionalEvents={regionalEvents}
                                        selectedRegionalEvents={studentPageElement?.regionalEvents}
                                        selectedRegionalEventProfileTypeId={
                                          studentPageElement?.regionalEventProfileTypeId
                                        }
                                        setRegionalEventProfileType={setRegionalEventProfileType}
                                        selectRegionalEvent={selectRegionalEvent}
                                        subInputField={studentPageElement?.subInputField}
                                        updateSubInputField={updateSubInputField}
                                        infoPopupHeading={studentPageElement?.heading}
                                        infoPopupSubHeading={studentPageElement?.subHeading}
                                        infoPopupHeadingColor={studentPageElement?.headingColor}
                                        infoPopupSubHeadingColor={
                                          studentPageElement?.subHeadingColor
                                        }
                                        infoPopupBackgroundColor={
                                          studentPageElement?.backgroundColor
                                        }
                                        infoPopupCloseIconBackgroundColor={
                                          studentPageElement?.closeIconBackgroundColor
                                        }
                                        infoPopupCloseIconColor={studentPageElement?.closeIconColor}
                                        infoPopupButtonBackgroundColor={
                                          studentPageElement?.buttonBackgroundColor
                                        }
                                        infoPopupButtonTextColor={
                                          studentPageElement?.buttonTextColor
                                        }
                                        infoPopupAutoShow={studentPageElement?.autoShowPopup}
                                        updateInfoPopupField={updateInfoPopupField}
                                        similarProfileBadgesNavigationPopup={
                                          studentPageElement?.navigationPopup
                                        }
                                        updateSimilarProfileBadgesNavigationPopupField={
                                          updateSimilarProfileBadgesNavigationPopupField
                                        }
                                      />
                                    </div>
                                  )}
                                </Draggable>
                              )
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <ClassSudentComponentsPicker
                      type={"studentPage"}
                      projectVariant={variant}
                      addComponent={(type, projectVariant, value) =>
                        addClassStudentComponent(
                          type,
                          projectVariant,
                          value,
                          region,
                          externalProjectType
                        )
                      }
                    />
                  </div>
                </div>

                {/* Parent Webpage UI */}
                {!externalProjectType && (
                  <div className="px-4 py-3 bg-gray-100 rounded-lg mt-5">
                    <p
                      className="w-full text-lg font-bold my-2 cursor-pointer"
                      onClick={() => {
                        setDisplayToggle({
                          ...displayToggle,
                          [`${variant}-${region}`]: {
                            classPage: displayToggle?.[`${variant}-${region}`]?.classPage,
                            studentPage: displayToggle?.[`${variant}-${region}`]?.studentPage,
                            parentPage: !displayToggle?.[`${variant}-${region}`]?.parentPage,
                            teamProfilePage:
                              displayToggle?.[`${variant}-${region}`]?.teamProfilePage
                          }
                        });
                      }}>
                      Parent Webpage UI
                    </p>
                    <div
                      className={`${
                        displayToggle?.[`${variant}-${region}`]?.parentPage ? "block" : "hidden"
                      }`}>
                      <DragDropContext
                        onDragEnd={(result) => {
                          onDragEndClassStudentSetup(
                            "parentPage",
                            variant,
                            result,
                            region,
                            externalProjectType
                          );
                        }}>
                        <Droppable droppableId={`parentPage-${region}-${variant}-components`}>
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              {variantDetails?.parentPage?.map(
                                (parentPageElement, parentPageElementIndex) => (
                                  <Draggable
                                    key={`parentPage-${variant}-${parentPageElement.id}-${parentPageElementIndex}`}
                                    draggableId={`parentPage-${region}-${variant}-${parentPageElement.id}-${parentPageElementIndex}`}
                                    index={parentPageElementIndex}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        id={`parentPage-${region}-${variant}-${parentPageElement.id}-${parentPageElementIndex}`}
                                        className="my-5">
                                        <ClassStudentComponents
                                          index={parentPageElementIndex}
                                          region={region}
                                          externalProjectType={externalProjectType}
                                          type={"parentPage"}
                                          projectVariant={variant}
                                          projectVariantTitle={variantTitle}
                                          componentType={parentPageElement.id}
                                          backgroundHeading={parentPageElement.backgroundHeading}
                                          updateBackgroundHeading={updateBackgroundHeading}
                                          foregroundHeading={parentPageElement.foregroundHeading}
                                          updateForegroundHeading={updateForegroundHeading}
                                          subText={parentPageElement.subText}
                                          updateSubText={updateSubText}
                                          buttonText={parentPageElement.buttonText}
                                          updateButtonText={updateButtonText}
                                          html={parentPageElement.html}
                                          updateHTMLBox={updateHTMLBox}
                                          videos={parentPageElement.videos}
                                          updateVideosPlayerTitle={updateVideosPlayerTitle}
                                          updateVideosPlayerUrl={updateVideosPlayerUrl}
                                          addNewVideoInVideosPlayer={addNewVideoInVideosPlayer}
                                          deleteVideoInVideosPlayer={deleteVideoInVideosPlayer}
                                          videPlayerAptitudeCloudEnabled={
                                            parentPageElement?.enableAttitudeCloud
                                          }
                                          toggleVideoPlayerAptitudeCloud={
                                            toggleVideoPlayerAptitudeCloud
                                          }
                                          regions={regions}
                                          selectedRegionalLinks={
                                            parentPageElement?.selectedRegions || []
                                          }
                                          selectProjectTypeRegionalLink={
                                            selectProjectTypeRegionalLink
                                          }
                                          availableUsersGroups={systemAdminGroups}
                                          selectedUsersGroups={
                                            parentPageElement?.selectedUsersGroups
                                          }
                                          discoverChampionCustomUrlValue={
                                            parentPageElement?.discoverChampionCustomUrl
                                          }
                                          selectUsersGroup={selectUsersGroup}
                                          discoverChampionCustomUrl={discoverChampionCustomUrl}
                                          professionTypes={professionTypes}
                                          selectedProfessionType={parentPageElement?.professionType}
                                          selectProfessionType={selectProfessionType}
                                          removeComponent={deleteClassStudentComponent}
                                          hideInPdf={parentPageElement?.hideInPdf}
                                          toggleShowClassStudentComponentInPdf={
                                            toggleShowClassStudentComponentInPdf
                                          }
                                          updateListLength={updateListLength}
                                          listLength={parentPageElement?.listLength}
                                          replacePreviousSelections={
                                            parentPageElement?.replacePreviousSelections
                                          }
                                          toggleReplacePreviousSelections={
                                            toggleReplacePreviousSelections
                                          }
                                          columns={parentPageElement?.columns}
                                          addTableColumn={addTableColumn}
                                          updateTableColumn={updateTableColumn}
                                          deleteTableColumn={deleteTableColumn}
                                          addAptitudeCloudOption={addAptitudeCloudOption}
                                          deleteAptitudeCloudOption={deleteAptitudeCloudOption}
                                          updateAptitudeCloudOption={updateAptitudeCloudOption}
                                          aptitudeCloudOptions={
                                            parentPageElement?.aptitudeCloudOptions
                                          }
                                          updateStudentRandomInputsNumber={
                                            updateStudentRandomInputsNumber
                                          }
                                          studentRandomInputsNumber={
                                            parentPageElement?.numberOfInputs
                                          }
                                          studentRandomInputsPopupHeading={
                                            parentPageElement?.popupHeading
                                          }
                                          studentRandomInputsPopupSubmitButtonText={
                                            parentPageElement?.popupSubmitButtonText
                                          }
                                          studentRandomInputsPopupInputPlaceholder={
                                            parentPageElement?.popupInputPlaceholder
                                          }
                                          studentRandomInputsWordCloudId={
                                            parentPageElement?.studentRandomInputsWordCloudId
                                          }
                                          updateStudentRandomInputsField={
                                            updateStudentRandomInputsField
                                          }
                                          studentRandomInputsOptions={parentPageElement?.options}
                                          addStudentRandomInputsOption={
                                            addStudentRandomInputsOption
                                          }
                                          removeStudentRandomInputsOption={
                                            removeStudentRandomInputsOption
                                          }
                                          updateStudentRandomInputsOption={
                                            updateStudentRandomInputsOption
                                          }
                                          totalBadgesToShow={parentPageElement?.totalBadgesToShow}
                                          updateTotalBadgesToShow={updateTotalBadgesToShow}
                                          studentAttributesToMatch={
                                            parentPageElement?.studentAttributesToMatch
                                          }
                                          selectStudentAttributesToMatch={
                                            selectStudentAttributesToMatch
                                          }
                                          defaultStudentAttributesToMatch={
                                            defaulTStudentAttributesToMatch
                                          }
                                          regionalEvents={regionalEvents}
                                          selectedRegionalEvents={parentPageElement?.regionalEvents}
                                          selectedRegionalEventProfileTypeId={
                                            parentPageElement?.regionalEventProfileTypeId
                                          }
                                          setRegionalEventProfileType={setRegionalEventProfileType}
                                          selectRegionalEvent={selectRegionalEvent}
                                          similarProfileBadgesNavigationPopup={
                                            parentPageElement?.navigationPopup
                                          }
                                          updateSimilarProfileBadgesNavigationPopupField={
                                            updateSimilarProfileBadgesNavigationPopupField
                                          }
                                          subInputField={parentPageElement?.subInputField}
                                          updateSubInputField={updateSubInputField}
                                          infoPopupHeading={parentPageElement?.heading}
                                          infoPopupSubHeading={parentPageElement?.subHeading}
                                          infoPopupHeadingColor={parentPageElement?.headingColor}
                                          infoPopupSubHeadingColor={
                                            parentPageElement?.subHeadingColor
                                          }
                                          infoPopupBackgroundColor={
                                            parentPageElement?.backgroundColor
                                          }
                                          infoPopupCloseIconBackgroundColor={
                                            parentPageElement?.closeIconBackgroundColor
                                          }
                                          infoPopupCloseIconColor={
                                            parentPageElement?.closeIconColor
                                          }
                                          infoPopupButtonBackgroundColor={
                                            parentPageElement?.buttonBackgroundColor
                                          }
                                          infoPopupButtonTextColor={
                                            parentPageElement?.buttonTextColor
                                          }
                                          infoPopupAutoShow={parentPageElement?.autoShowPopup}
                                          updateInfoPopupField={updateInfoPopupField}
                                          downloadType={parentPageElement?.downloadType}
                                          updateDownloadType={updateDownloadType}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                      <ClassSudentComponentsPicker
                        type={"parentPage"}
                        projectVariant={variant}
                        addComponent={(type, projectVariant, value) =>
                          addClassStudentComponent(
                            type,
                            projectVariant,
                            value,
                            region,
                            externalProjectType
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* List Team Profile page components */}
                {hasTeamProfilePageEnabled && (
                  <div className="px-4 py-3 bg-gray-100 rounded-lg mt-5">
                    <p
                      className="w-full text-lg font-bold my-2 cursor-pointer"
                      onClick={() => {
                        setDisplayToggle({
                          ...displayToggle,
                          [`${variant}-${region}`]: {
                            classPage: displayToggle?.[`${variant}-${region}`]?.classPage,
                            studentPage: displayToggle?.[`${variant}-${region}`]?.studentPage,
                            parentPage: displayToggle?.[`${variant}-${region}`]?.parentPage,
                            teamProfilePage:
                              !displayToggle?.[`${variant}-${region}`]?.teamProfilePage
                          }
                        });
                      }}>
                      Team Profile Components
                    </p>
                    <div
                      className={`${
                        displayToggle?.[`${variant}-${region}`]?.teamProfilePage
                          ? "block"
                          : "hidden"
                      }`}>
                      <DragDropContext
                        onDragEnd={(result) => {
                          onDragEndClassStudentSetup("teamProfilePage", variant, result, region);
                        }}>
                        <Droppable droppableId={`teamProfilePage-${region}-${variant}-components`}>
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              {variantDetails?.teamProfilePage?.map(
                                (teamProfileElement, teamProfileElementIndex) => (
                                  <Draggable
                                    key={`teamProfilePage-${variant}-${teamProfileElement.id}-${teamProfileElementIndex}`}
                                    draggableId={`teamProfilePage-${region}-${variant}-${teamProfileElement.id}-${teamProfileElementIndex}`}
                                    index={teamProfileElementIndex}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        id={`teamProfilePage-${region}-${variant}-${teamProfileElement.id}-${teamProfileElementIndex}`}
                                        className="my-5">
                                        <ClassStudentComponents
                                          index={teamProfileElementIndex}
                                          region={region}
                                          externalProjectType={externalProjectType}
                                          type={"teamProfilePage"}
                                          projectVariant={variant}
                                          projectVariantTitle={variantTitle}
                                          componentType={teamProfileElement.id}
                                          backgroundHeading={teamProfileElement.backgroundHeading}
                                          updateBackgroundHeading={updateBackgroundHeading}
                                          foregroundHeading={teamProfileElement.foregroundHeading}
                                          updateForegroundHeading={updateForegroundHeading}
                                          subText={teamProfileElement.subText}
                                          updateSubText={updateSubText}
                                          buttonText={teamProfileElement.buttonText}
                                          updateButtonText={updateButtonText}
                                          html={teamProfileElement.html}
                                          updateHTMLBox={updateHTMLBox}
                                          videos={teamProfileElement.videos}
                                          updateVideosPlayerTitle={updateVideosPlayerTitle}
                                          updateVideosPlayerUrl={updateVideosPlayerUrl}
                                          addNewVideoInVideosPlayer={addNewVideoInVideosPlayer}
                                          deleteVideoInVideosPlayer={deleteVideoInVideosPlayer}
                                          videPlayerAptitudeCloudEnabled={
                                            teamProfileElement?.enableAttitudeCloud
                                          }
                                          toggleVideoPlayerAptitudeCloud={
                                            toggleVideoPlayerAptitudeCloud
                                          }
                                          regions={regions}
                                          selectedRegionalLinks={
                                            teamProfileElement?.selectedRegions || []
                                          }
                                          selectProjectTypeRegionalLink={
                                            selectProjectTypeRegionalLink
                                          }
                                          availableUsersGroups={systemAdminGroups}
                                          selectedUsersGroups={
                                            teamProfileElement?.selectedUsersGroups
                                          }
                                          selectUsersGroup={selectUsersGroup}
                                          discoverChampionCustomUrlValue={
                                            teamProfileElement?.discoverChampionCustomUrl
                                          }
                                          discoverChampionCustomUrl={discoverChampionCustomUrl}
                                          professionTypes={professionTypes}
                                          selectedProfessionType={
                                            teamProfileElement?.professionType
                                          }
                                          selectProfessionType={selectProfessionType}
                                          removeComponent={deleteClassStudentComponent}
                                          hideInPdf={teamProfileElement?.hideInPdf}
                                          toggleShowClassStudentComponentInPdf={
                                            toggleShowClassStudentComponentInPdf
                                          }
                                          useOrganizationStrengthsListInAptitudeCloud={
                                            teamProfileElement?.useOrganizationStrengthsList
                                          }
                                          toggleUseOrganizationStrengthsListInAptitudeCloud={
                                            toggleUseOrganizationStrengthsListInAptitudeCloud
                                          }
                                          useOrganizationRolesListInAptitudeCloud={
                                            teamProfileElement?.useOrganizationRolesList
                                          }
                                          toggleUseOrganizationRolesListInAptitudeCloud={
                                            toggleUseOrganizationRolesListInAptitudeCloud
                                          }
                                          useOrganizationProfessionsListInAptitudeCloud={
                                            teamProfileElement?.useOrganizationProfessionsList
                                          }
                                          toggleUseOrganizationProfessionsListInAptitudeCloud={
                                            toggleUseOrganizationProfessionsListInAptitudeCloud
                                          }
                                          showAsList={teamProfileElement?.showAsList}
                                          toggleShowAsListInAptitudeCloud={
                                            toggleShowAsListInAptitudeCloud
                                          }
                                          updateListLength={updateListLength}
                                          listLength={teamProfileElement?.listLength}
                                          replacePreviousSelections={
                                            teamProfileElement?.replacePreviousSelections
                                          }
                                          toggleReplacePreviousSelections={
                                            toggleReplacePreviousSelections
                                          }
                                          columns={teamProfileElement?.columns}
                                          addTableColumn={addTableColumn}
                                          updateTableColumn={updateTableColumn}
                                          deleteTableColumn={deleteTableColumn}
                                          attitudesList={teamProfileElement?.attitudesList}
                                          addAttitudeListItem={addAttitudeListItem}
                                          updateAttitudeListItem={updateAttitudeListItem}
                                          deleteAttitudeListItem={deleteAttitudeListItem}
                                          updateAttitudeComponentField={
                                            updateAttitudeComponentField
                                          }
                                          attitudesTitle={teamProfileElement?.title}
                                          attitudesSubtitle={teamProfileElement?.subtitle}
                                          attitudesTitleNumber2={teamProfileElement?.titleNumber2}
                                          attitudesSubtitleNumber2={
                                            teamProfileElement?.subtitleNumber2
                                          }
                                          attitudesSubmitButtonText={
                                            teamProfileElement?.submitButtonText
                                          }
                                          addAptitudeCloudOption={addAptitudeCloudOption}
                                          deleteAptitudeCloudOption={deleteAptitudeCloudOption}
                                          updateAptitudeCloudOption={updateAptitudeCloudOption}
                                          aptitudeCloudOptions={
                                            teamProfileElement?.aptitudeCloudOptions
                                          }
                                          updateStudentRandomInputsNumber={
                                            updateStudentRandomInputsNumber
                                          }
                                          studentRandomInputsNumber={
                                            teamProfileElement?.numberOfInputs
                                          }
                                          studentRandomInputsPopupHeading={
                                            teamProfileElement?.popupHeading
                                          }
                                          studentRandomInputsPopupSubmitButtonText={
                                            teamProfileElement?.popupSubmitButtonText
                                          }
                                          studentRandomInputsPopupInputPlaceholder={
                                            teamProfileElement?.popupInputPlaceholder
                                          }
                                          studentRandomInputsWordCloudId={
                                            teamProfileElement?.studentRandomInputsWordCloudId
                                          }
                                          updateStudentRandomInputsField={
                                            updateStudentRandomInputsField
                                          }
                                          studentRandomInputsOptions={teamProfileElement?.options}
                                          addStudentRandomInputsOption={
                                            addStudentRandomInputsOption
                                          }
                                          removeStudentRandomInputsOption={
                                            removeStudentRandomInputsOption
                                          }
                                          updateStudentRandomInputsOption={
                                            updateStudentRandomInputsOption
                                          }
                                          totalBadgesToShow={teamProfileElement?.totalBadgesToShow}
                                          updateTotalBadgesToShow={updateTotalBadgesToShow}
                                          studentAttributesToMatch={
                                            teamProfileElement?.studentAttributesToMatch
                                          }
                                          selectStudentAttributesToMatch={
                                            selectStudentAttributesToMatch
                                          }
                                          defaultStudentAttributesToMatch={
                                            defaulTStudentAttributesToMatch
                                          }
                                          similarProfileBadgesNavigationPopup={
                                            teamProfileElement?.navigationPopup
                                          }
                                          updateSimilarProfileBadgesNavigationPopupField={
                                            updateSimilarProfileBadgesNavigationPopupField
                                          }
                                          subInputField={teamProfileElement?.subInputField}
                                          updateSubInputField={updateSubInputField}
                                          infoPopupHeading={teamProfileElement?.heading}
                                          infoPopupSubHeading={teamProfileElement?.subHeading}
                                          infoPopupHeadingColor={teamProfileElement?.headingColor}
                                          infoPopupSubHeadingColor={
                                            teamProfileElement?.subHeadingColor
                                          }
                                          infoPopupBackgroundColor={
                                            teamProfileElement?.backgroundColor
                                          }
                                          infoPopupCloseIconBackgroundColor={
                                            teamProfileElement?.closeIconBackgroundColor
                                          }
                                          infoPopupCloseIconColor={
                                            teamProfileElement?.closeIconColor
                                          }
                                          infoPopupButtonBackgroundColor={
                                            teamProfileElement?.buttonBackgroundColor
                                          }
                                          infoPopupButtonTextColor={
                                            teamProfileElement?.buttonTextColor
                                          }
                                          infoPopupAutoShow={teamProfileElement?.autoShowPopup}
                                          updateInfoPopupField={updateInfoPopupField}
                                        />
                                      </div>
                                    )}
                                  </Draggable>
                                )
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                      <ClassSudentComponentsPicker
                        type={"teamProfilePage"}
                        projectVariant={variant}
                        addComponent={(type, projectVariant, value) =>
                          addClassStudentComponent(
                            type,
                            projectVariant,
                            value,
                            region,
                            externalProjectType
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="mt-5 flex gap-3 justify-end">
                  <Button.AdminPanelButton
                    type="submit"
                    isSave={true}
                    loading={false}
                    bgColor="#515151"
                    textColor="#FFFFFF"
                    color="white"
                    onClick={saveTeacherAdminSetup}>
                    Save
                  </Button.AdminPanelButton>
                  {variant !== "default" && Object.keys(classProjectTypes)?.length > 1 && (
                    <Button.AdminPanelButton
                      type="button"
                      isSave={true}
                      loading={false}
                      bgColor="#515151"
                      textColor="#FFFFFF"
                      color="white"
                      onClick={() => {
                        if (externalProjectType) {
                          const temp = _.cloneDeep(classProjectTypes);
                          delete temp[variant];
                          setClassProjectTypes(temp);
                        } else {
                          deleteClassProject(variant, region);
                        }
                      }}>
                      Delete
                    </Button.AdminPanelButton>
                  )}
                  <Button.AdminPanelButton
                    type="button"
                    isSave={true}
                    loading={false}
                    bgColor="#515151"
                    textColor="#FFFFFF"
                    color="white"
                    onClick={() => {
                      if (externalProjectType) {
                        const temp = _.cloneDeep(classProjectTypes)?.[variant];
                        const newProjectId = uuidv4();
                        temp.projectId = newProjectId;
                        temp.title.en += " (Copy)";
                        setClassProjectTypes({
                          ...classProjectTypes,
                          [newProjectId]: temp
                        });
                      } else {
                        duplicateClassProject(variant, region);
                      }
                    }}>
                    Duplicate single project type
                  </Button.AdminPanelButton>
                </div>
              </div>
            </div>
          );
        })}

      <div className="mt-5 flex justify-end gap-3">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={false}
          bgColor="#515151"
          textColor="#FFFFFF"
          color="white"
          onClick={saveTeacherAdminSetup}>
          Save
        </Button.AdminPanelButton>
        {regionalProjectTypes && Object.keys(regionalProjectTypes).length !== regions.length && (
          <Button.AdminPanelButton
            type="button"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={() =>
              duplicateClassProjectTypeForRegion(
                classProjectTypes,
                regions
                  .map((region) => region._id)
                  .filter((region) => !Object.keys(regionalProjectTypes).includes(region))?.[0]
              )
            }>
            Duplicate for new region
          </Button.AdminPanelButton>
        )}
        {/* Delete region */}
        {regionalProjectTypes && region !== "default" && (
          <Button.AdminPanelButton
            type="button"
            isSave={true}
            loading={false}
            bgColor="#515151"
            textColor="#FFFFFF"
            color="white"
            onClick={() => deleteRegionalClassProjectType(region)}>
            Delete regional project types
          </Button.AdminPanelButton>
        )}
      </div>

      <BaseModal
        modalSize="max-w-xl w-full"
        open={vipThemeModalType === "themeSetup"}
        setOpen={() => setVipThemeModalType(null)}
        title={vipThemeModalType === "themeSetup" ? "Theme Setup" : "Components Setup"}>
        <CustomVIPConfigThemeSetup
          savedValues={vipThemeModalSavedValues}
          setValues={setVipThemeModalSavedValues}
          storeVipConfigTheme={storeVipConfigTheme}
        />
      </BaseModal>
    </div>
  );
};

export default ClassProjectSetup;

const CustomVIPConfig = ({
  showElement,
  setShowElement,
  title = "VIP Configuration",
  configKey = "vipConfig",
  org,
  serverBaseUrl,
  externalProjectType,
  variantDetails,
  emailTemplates,
  toggleSendEmailAfterCompletion,
  setEmailTemplateId,
  setCustomVIPProductId,
  setVIPTestUrlPath,
  variant,
  region,
  setVIPConfigProfileSetupType,
  toggleParentsCustomVipTest,
  openVipConfigThemeModal,
  toggleVipConfigPage,
  addVipConfigStepPage,
  removeVipConfigStepPage,
  addVipConfigPageComponent,
  deleteVipConfigPageComponent,
  onDragEndVipConfigPageComponent,
  updateKeyValueInVipConfigPageComponent,
  setVIPServerUrl,
  systemAdminGroups,
  setProjectGroup,
  showParentsCustomVipToggle = true,
  showProductIdInput = true
}) => {
  const [editComponents, setEditComponents] = useState(null);
  const [showStepPages, setShowStepPages] = useState(false);
  const vipConfig = variantDetails?.[configKey] || {};

  useEffect(() => {
    if (externalProjectType) {
      if (variantDetails.projectFor === "class") setShowStepPages(true);
      else setShowStepPages(false);
    } else {
      setShowStepPages(true);
    }
  }, [externalProjectType, variantDetails.projectFor]);

  return (
    <div className={`my-10 border-[1px] border-[#606060] rounded-md p-4 bg-[#FF9E441F] `}>
      <p
        className="font-bold text-lg mb-2 cursor-pointer select-none"
        onClick={() => setShowElement({ ...showElement, [configKey]: !showElement?.[configKey] })}>
        {title}
      </p>
      <div className={`${showElement?.[configKey] ? "block" : "hidden"}`}>
        {!externalProjectType && showProductIdInput && (
          <Input
            className={"w-full mb-4"}
            inputClassName={"rounded-full border-none bg-[#fff]"}
            labelClassName={"text-sm font-normal text-[#606060]"}
            label={"Custom Product ID (zero means no custom product id)"}
            placeholder={"25"}
            type="text"
            value={vipConfig?.customVIPProductId || 0}
            onChange={(e) => {
              setCustomVIPProductId &&
                setCustomVIPProductId(
                  variant,
                  region,
                  Number(e.target.value),
                  externalProjectType,
                  configKey
                );
            }}
          />
        )}

        {externalProjectType && (
          <Input
            className={"w-full mb-4"}
            inputClassName={"rounded-full border-none bg-[#fff]"}
            labelClassName={"text-sm font-normal text-[#606060]"}
            label={"Server URL (leave blank if you want to use the default server url)"}
            placeholder={"https://staging.psyfiers.ch/core/"}
            // fixedPrefix={serverBaseUrl}
            type="text"
            value={vipConfig?.serverUrl || ""}
            onChange={(e) => {
              setVIPServerUrl &&
                setVIPServerUrl(variant, region, e.target.value, externalProjectType, configKey);
            }}
            copyable={false}
          />
        )}

        {externalProjectType && (
          <Input
            className={"w-full mb-4"}
            inputClassName={"rounded-full border-none bg-[#fff]"}
            labelClassName={"text-sm font-normal text-[#606060]"}
            label={"VIP Test URL"}
            placeholder={"https://staging.psyfiers.ch/missionfuturewskfrance230/559"}
            // fixedPrefix={serverBaseUrl}
            type="text"
            value={vipConfig?.vipTestUrlPath || ""}
            onChange={(e) => {
              setVIPTestUrlPath &&
                setVIPTestUrlPath(variant, region, e.target.value, externalProjectType, configKey);
            }}
            copyable={true}
          />
        )}

        {variantDetails?.projectFor === "champions" && (
          <div>
            <label className="block font-semibold text-blue-700 mb-2">Profile Group</label>
            <InputSelect
              name="profileGroup"
              placeholder="Select a profile group..."
              options={[
                { value: "", label: "No profile group selected" },
                ...(systemAdminGroups || []).map((template) => ({
                  value: template._id,
                  label: template.name || "Untitled Profile Group"
                }))
              ]}
              value={
                variantDetails?.projectGroupId
                  ? {
                      value: variantDetails.projectGroupId,
                      label:
                        systemAdminGroups?.find((t) => t._id === variantDetails.projectGroupId)
                          ?.name || "Selected Profile Group"
                    }
                  : { value: "", label: "No profile group selected" }
              }
              onChange={(selectedOption) => {
                setProjectGroup && setProjectGroup(variant, selectedOption?.value || "");
              }}
            />
          </div>
        )}

        {variantDetails?.projectFor !== "champions" && (
          <RadioGroupInput
            srLabel="VIP Configuration"
            options={[
              {
                id: "vipTestAndProfileSetup",
                name: "VIP Test + Profile Setup",
                description: "Includes a test and a profile setup page"
              },
              {
                id: "vipTestOnly",
                name: "VIP Test Only",
                description: "Includes a test page only"
              }
            ]}
            onSelect={(id) => {
              setVIPConfigProfileSetupType &&
                setVIPConfigProfileSetupType(variant, region, id, externalProjectType, configKey);
            }}
            preselected={vipConfig?.profileSetupType}
          />
        )}

        {showParentsCustomVipToggle && configKey === "vipConfig" && (
          <div className="flex items-center justify-between w-full mt-4">
            <p className="font-semibold text-gray-600">Parents custom VIP test</p>
            <ToggleComponent
              handleCompToggle={() =>
                toggleParentsCustomVipTest &&
                toggleParentsCustomVipTest(variant, region, externalProjectType, "vipConfig")
              }
              isToggle={variantDetails?.vipConfig?.parentsCustomVipTest || false}
            />
          </div>
        )}

        <p className="font-bold text-lg mb-2 cursor-pointer select-none mt-4">Frontend Setup</p>
        <div className="flex flex-col gap-2 w-full">
          <div className="flex items-center justify-between w-full pl-4 mb-4">
            <p className="font-semibold text-gray-600">Theme Setup</p>
            <div className="flex gap-3 items-center justify-between">
              <div className="flex gap-3 items-center">
                <button
                  className="cursor-pointer"
                  onClick={() =>
                    openVipConfigThemeModal &&
                    openVipConfigThemeModal(
                      "themeSetup",
                      vipConfig?.themeSetup,
                      variant,
                      region,
                      null,
                      configKey
                    )
                  }>
                  <PencilIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div className="w-full pl-4">
            <div className="w-full mb-4 flex items-center justify-between">
              <p className="font-semibold text-gray-600">Landing Page</p>
              <div className="flex gap-3 items-center justify-between">
                <div className="flex gap-3 items-center">
                  <ToggleComponent
                    handleCompToggle={() =>
                      toggleVipConfigPage &&
                      toggleVipConfigPage(
                        variant,
                        region,
                        "landingPage",
                        null,
                        externalProjectType,
                        configKey
                      )
                    }
                    isToggle={vipConfig?.landingPage?.enabled}
                  />
                  <button
                    className="cursor-pointer"
                    onClick={() =>
                      editComponents === "landingPage"
                        ? setEditComponents(null)
                        : setEditComponents("landingPage")
                    }>
                    {editComponents === "landingPage" ? (
                      <RxExit className="w-4 h-4" />
                    ) : (
                      <PencilIcon className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {editComponents === "landingPage" && (
              <CustomVIPConfigPageComponentsSetup
                org={org}
                externalProjectType={externalProjectType}
                pageId={"landingPage"}
                variant={variant}
                region={region}
                stepPageIndex={null}
                components={vipConfig?.landingPage?.components}
                configKey={configKey}
                addVipConfigPageComponent={addVipConfigPageComponent}
                deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
              />
            )}
          </div>
          <div className="w-full pl-4">
            {showStepPages && (
              <div className="w-full mb-4 flex items-center justify-between">
                <p className="font-semibold text-gray-600">Starting Page</p>
                <div className="flex gap-3 items-center justify-between">
                  <div className="flex gap-3 items-center">
                    <ToggleComponent
                      handleCompToggle={() =>
                        toggleVipConfigPage &&
                        toggleVipConfigPage(
                          variant,
                          region,
                          "startingPage",
                          null,
                          externalProjectType,
                          configKey
                        )
                      }
                      isToggle={vipConfig?.startingPage?.enabled}
                    />
                    <button
                      className="cursor-pointer"
                      onClick={() =>
                        editComponents === "startingPage"
                          ? setEditComponents(null)
                          : setEditComponents("startingPage")
                      }>
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {editComponents === "startingPage" && (
              <CustomVIPConfigPageComponentsSetup
                org={org}
                externalProjectType={externalProjectType}
                pageId={"startingPage"}
                variant={variant}
                region={region}
                stepPageIndex={null}
                components={vipConfig?.startingPage?.components}
                configKey={configKey}
                addVipConfigPageComponent={addVipConfigPageComponent}
                deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
              />
            )}
          </div>

          {showStepPages &&
            vipConfig?.stepPages?.map((stepPage, index) => {
              return (
                <div key={index} className="w-full pl-4">
                  <div className="w-full mb-4 flex items-center justify-between">
                    <p className="font-semibold text-gray-600">Step Page {index + 1}</p>
                    <div className="flex gap-3 items-center justify-between">
                      <div className="flex gap-3 items-center">
                        <ToggleComponent
                          handleCompToggle={() =>
                            toggleVipConfigPage &&
                            toggleVipConfigPage(
                              variant,
                              region,
                              `stepPageSetupEnabled-${index}`,
                              index,
                              externalProjectType,
                              configKey
                            )
                          }
                          isToggle={vipConfig?.stepPages?.[index]?.enabled}
                        />
                        <button
                          className="cursor-pointer"
                          onClick={() =>
                            editComponents === `stepPage-${index}`
                              ? setEditComponents(null)
                              : setEditComponents(`stepPage-${index}`)
                          }>
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="cursor-pointer"
                          onClick={() =>
                            removeVipConfigStepPage &&
                            removeVipConfigStepPage(
                              variant,
                              region,
                              index,
                              configKey,
                              externalProjectType
                            )
                          }>
                          <TrashIcon className="w-6 h-6 text-red-500 hover:text-red-700 transition-all bg-red-200 rounded-full p-1 hover:scale-110 active:scale-95" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {editComponents === `stepPage-${index}` && (
                    <CustomVIPConfigPageComponentsSetup
                      org={org}
                      externalProjectType={externalProjectType}
                      pageId={"stepPage"}
                      variant={variant}
                      region={region}
                      stepPageIndex={index}
                      components={stepPage?.components}
                      configKey={configKey}
                      addVipConfigPageComponent={addVipConfigPageComponent}
                      deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                      onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                      updateKeyValueInVipConfigPageComponent={
                        updateKeyValueInVipConfigPageComponent
                      }
                    />
                  )}
                </div>
              );
            })}

          {showStepPages && (
            <button
              className="text-blue-500 border border-blue-500 hover:bg-blue-500 hover:text-white rounded-md px-2 py-1"
              onClick={() => {
                addVipConfigStepPage &&
                  addVipConfigStepPage(variant, region, externalProjectType, configKey);
              }}>
              Add Step Page
            </button>
          )}

          <div className="w-full pl-4">
            {showStepPages && (
              <div className="w-full mb-4 flex items-center justify-between">
                <p className="font-semibold text-gray-600">Final Page</p>
                <div className="flex gap-3 items-center justify-between">
                  <div className="flex gap-3 items-center">
                    <ToggleComponent
                      handleCompToggle={() =>
                        toggleVipConfigPage &&
                        toggleVipConfigPage(
                          variant,
                          region,
                          "finalPage",
                          null,
                          externalProjectType,
                          configKey
                        )
                      }
                      isToggle={vipConfig?.finalPage?.enabled}
                    />
                    <button
                      className="cursor-pointer"
                      onClick={() =>
                        editComponents === "finalPage"
                          ? setEditComponents(null)
                          : setEditComponents("finalPage")
                      }>
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {editComponents === "finalPage" && (
              <CustomVIPConfigPageComponentsSetup
                org={org}
                externalProjectType={externalProjectType}
                pageId={"finalPage"}
                variant={variant}
                region={region}
                stepPageIndex={null}
                components={vipConfig?.finalPage?.components}
                configKey={configKey}
                addVipConfigPageComponent={addVipConfigPageComponent}
                deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                onDragEndVipConfigPageComponent={onDragEndVipConfigPageComponent}
                updateKeyValueInVipConfigPageComponent={updateKeyValueInVipConfigPageComponent}
              />
            )}
          </div>
        </div>

        {/* Email Configuration for External Projects */}
        {(externalProjectType || configKey === "parentVipConfig") && (
          <div className="my-6 p-4 border border-blue-200 rounded-lg bg-blue-50">
            <h3 className="font-bold text-lg mb-4 text-blue-800">Email Configuration</h3>

            <div className="flex items-center justify-between mb-4">
              <label className="font-semibold text-blue-700">Send Email After Completion</label>
              <ToggleComponent
                handleCompToggle={() =>
                  toggleSendEmailAfterCompletion &&
                  toggleSendEmailAfterCompletion(variant, region, externalProjectType, configKey)
                }
                isToggle={variantDetails?.[configKey]?.sendEmailAfterCompletion || false}
              />
            </div>

            {variantDetails?.[configKey]?.sendEmailAfterCompletion && (
              <div>
                <label className="block font-semibold text-blue-700 mb-2">Email Template</label>
                <InputSelect
                  name="emailTemplate"
                  placeholder="Select an email template..."
                  options={[
                    { value: "", label: "No template selected" },
                    ...(emailTemplates || []).map((template) => ({
                      value: template._id,
                      label: template.title || "Untitled Template"
                    }))
                  ]}
                  value={
                    variantDetails?.[configKey]?.emailTemplateId
                      ? {
                          value: variantDetails[configKey].emailTemplateId,
                          label:
                            emailTemplates?.find(
                              (t) => t._id === variantDetails[configKey].emailTemplateId
                            )?.title || "Selected Template"
                        }
                      : { value: "", label: "No template selected" }
                  }
                  onChange={(selectedOption) => {
                    setEmailTemplateId &&
                      setEmailTemplateId(
                        variant,
                        region,
                        selectedOption?.value || "",
                        externalProjectType,
                        configKey
                      );
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectResources = ({
  externalProjectType,
  variantDetails,
  variant,
  region,
  updateProjectResources,
  showElement,
  setShowElement,
  languages
}) => {
  const [videoUploading, setVideoUploading] = useState({});
  const [videoUploadProgress, setVideoUploadProgress] = useState({});
  const [projectFilesUploading, setProjectFilesUploading] = useState({});
  const [projectFilesUploadProgress, setProjectFilesUploadProgress] = useState({});
  const [pdfUploading, setPdfUploading] = useState({});
  const [pdfUploadProgress, setPdfUploadProgress] = useState({});

  const handleVideoUpload = async (file, lang) => {
    if (!file) return;
    try {
      setVideoUploading((prev) => ({ ...prev, [lang]: true }));
      const videoUrl = await uploadFileIfNeeded(file, "project-resources", (progress) => {
        setVideoUploadProgress((prev) => ({ ...prev, [lang]: progress?.progress || 0 }));
      });
      updateProjectResources(
        variant,
        region,
        `demoVideo.url.${lang}`,
        videoUrl,
        null,
        externalProjectType
      );
      setVideoUploading((prev) => ({ ...prev, [lang]: false }));
      setVideoUploadProgress((prev) => ({ ...prev, [lang]: 0 }));
    } catch (error) {
      console.error("Video upload error:", error);
      setVideoUploading((prev) => ({ ...prev, [lang]: false }));
      setVideoUploadProgress((prev) => ({ ...prev, [lang]: 0 }));
    }
  };

  const handleProjectFileUpload = async (file, index, lang) => {
    if (!file) return;
    const key = `${index}_${lang}`;
    try {
      setProjectFilesUploading((prev) => ({ ...prev, [key]: true }));
      const fileUrl = await uploadFileIfNeeded(file, "project-files", (progress) => {
        setProjectFilesUploadProgress((prev) => ({ ...prev, [key]: progress?.progress || 0 }));
      });
      updateProjectResources(
        variant,
        region,
        `projectFiles.${index}.url.${lang}`,
        fileUrl,
        null,
        externalProjectType
      );
      setProjectFilesUploading((prev) => ({ ...prev, [key]: false }));
      setProjectFilesUploadProgress((prev) => ({ ...prev, [key]: 0 }));
    } catch (error) {
      console.error("Project file upload error:", error);
      setProjectFilesUploading((prev) => ({ ...prev, [key]: false }));
      setProjectFilesUploadProgress((prev) => ({ ...prev, [key]: 0 }));
    }
  };

  const handleProjectFileMove = (index, direction) => {
    const newProjectFiles = [...resources?.projectFiles];
    const file = newProjectFiles[index];
    newProjectFiles.splice(index, 1);
    newProjectFiles.splice(index + (direction === "up" ? -1 : 1), 0, file);
    updateProjectResources(
      variant,
      region,
      "projectFiles",
      newProjectFiles,
      null,
      externalProjectType
    );
  };

  const handleProjectFileRemove = (index) => {
    const newProjectFiles = [...resources?.projectFiles];
    newProjectFiles.splice(index, 1);
    updateProjectResources(
      variant,
      region,
      "projectFiles",
      newProjectFiles,
      null,
      externalProjectType
    );
  };

  const handlePdfUpload = async (file, lang) => {
    if (!file) return;
    try {
      setPdfUploading((prev) => ({ ...prev, [lang]: true }));
      const pdfUrl = await uploadFileIfNeeded(file, "project-resources", (progress) => {
        setPdfUploadProgress((prev) => ({ ...prev, [lang]: progress?.progress || 0 }));
      });
      updateProjectResources(
        variant,
        region,
        `vimeoAndPdfDownload.pdfLink.${lang}`,
        pdfUrl,
        null,
        externalProjectType
      );
      setPdfUploading((prev) => ({ ...prev, [lang]: false }));
      setPdfUploadProgress((prev) => ({ ...prev, [lang]: 0 }));
    } catch (error) {
      console.error("PDF upload error:", error);
      setPdfUploading((prev) => ({ ...prev, [lang]: false }));
      setPdfUploadProgress((prev) => ({ ...prev, [lang]: 0 }));
    }
  };

  const anyProjectFileUploading = Object.values(projectFilesUploading).some(Boolean);

  const resources = variantDetails?.resources || {
    demoVideo: { url: {}, html: {} },
    projectFiles: [
      {
        url: {},
        type: "",
        title: {},
        colors: { text: "", background: "" }
      }
    ],
    vimeoAndPdfDownload: { vimeoLink: {}, pdfLink: {}, studentVipPdfDownload: false }
  };

  return (
    <div className="my-10 border-[1px] border-[#606060] rounded-md p-4 bg-[#E8F5E9]">
      <p
        className="font-bold text-lg cursor-pointer select-none"
        onClick={() =>
          setShowElement({ ...showElement, projectResources: !showElement?.projectResources })
        }>
        Project Resources (Click to display/hide)
      </p>

      <div className="mt-4" style={{ display: showElement?.projectResources ? "block" : "none" }}>
        {/* Demo Video Upload */}
        <div className="mb-6">
          <p className="font-semibold text-gray-700 mb-2">Project Demo Video</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {languages.map((lang) => (
              <div key={lang} className="border border-gray-200 rounded-md p-3">
                <label className="text-sm font-medium text-gray-600 mb-2 uppercase block">
                  {lang}
                </label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoUpload(e.target.files[0], lang)}
                  className="cursor-pointer block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  disabled={videoUploading[lang]}
                />
                {videoUploading[lang] && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${videoUploadProgress[lang] * 100 || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Uploading: {Math.round(videoUploadProgress[lang] * 100 || 0)}%
                    </p>
                  </div>
                )}
                {resources?.demoVideo?.url?.[lang] && (
                  <div className="mt-3">
                    <video
                      src={process.env.REACT_APP_AWS_URL + resources.demoVideo.url[lang]}
                      controls
                      className="w-full rounded-lg border border-gray-300"
                      style={{ maxHeight: "200px" }}
                    />
                    <button
                      onClick={() =>
                        updateProjectResources(
                          variant,
                          region,
                          `demoVideo.url.${lang}`,
                          "",
                          null,
                          externalProjectType
                        )
                      }
                      className="mt-2 text-red-500 text-sm hover:underline">
                      Remove Video
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Demo Video HTML - 4 Languages */}
        <div className="mb-4">
          <p className="font-semibold text-gray-700 mb-3">Demo Video HTML Content</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {languages.map((lang) => (
              <div key={lang} className="flex flex-col">
                <label className="text-sm font-medium text-gray-600 mb-1 uppercase">{lang}</label>
                <textarea
                  className="w-full p-2 border border-gray-300 rounded-md min-h-[100px] text-sm"
                  placeholder={`Enter HTML content for ${lang.toUpperCase()}...`}
                  value={resources?.demoVideo?.html?.[lang] || ""}
                  onChange={(e) =>
                    updateProjectResources(
                      variant,
                      region,
                      `demoVideo.html.${lang}`,
                      e.target.value,
                      null,
                      externalProjectType
                    )
                  }
                />
              </div>
            ))}
          </div>
        </div>

        {/* Project Files */}
        <div className="mb-6">
          <p className="font-semibold text-gray-700 mb-2">Project Files</p>

          {resources?.projectFiles?.map((file, index) => {
            return (
              <div key={index} className="w-full border border-gray-300 rounded-md p-2 mb-2">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="flex items-center gap-2">
                    {index > 0 && (
                      <button
                        className="cursor-pointer p-1 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleProjectFileMove(index, "up")}
                        disabled={anyProjectFileUploading}>
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                    )}
                    {index < resources?.projectFiles?.length - 1 && (
                      <button
                        className="cursor-pointer p-1 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => handleProjectFileMove(index, "down")}
                        disabled={anyProjectFileUploading}>
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <button
                    className="text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleProjectFileRemove(index)}
                    disabled={anyProjectFileUploading}>
                    <TrashIcon className="w-4 h-4" />
                    Remove File
                  </button>
                </div>
                <p className="font-semibold text-gray-700 mb-2">File {index + 1}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {languages.map((lang) => {
                    const key = `${index}_${lang}`;
                    return (
                      <div key={lang} className="border border-gray-200 rounded-md p-2">
                        <label className="text-sm font-medium text-gray-600 mb-1 uppercase block">
                          {lang}
                        </label>
                        <input
                          type="file"
                          accept="*"
                          onChange={(e) => handleProjectFileUpload(e.target.files[0], index, lang)}
                          className="cursor-pointer block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                          disabled={projectFilesUploading[key]}
                        />
                        {projectFilesUploading[key] && (
                          <div className="mt-2">
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div
                                className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${projectFilesUploadProgress[key] * 100 || 0}%` }}
                              />
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              Uploading: {Math.round(projectFilesUploadProgress[key] * 100 || 0)}%
                            </p>
                          </div>
                        )}
                        {file?.url?.[lang] && (
                          <button
                            className="mt-2 text-green-700 bg-green-50 hover:bg-green-100 px-2 py-1 rounded-md flex items-center gap-2 text-sm"
                            onClick={() => {
                              try {
                                const fileFullUrl = process.env.REACT_APP_AWS_URL + file.url[lang];
                                const downloadLink = document.createElement("a");
                                downloadLink.href = fileFullUrl;
                                downloadLink.download = file?.name || "file";
                                downloadLink.target = "_blank";
                                downloadLink.click();
                              } catch (error) {
                                console.error("Error downloading file:", error);
                                toast.error("Error downloading file");
                              }
                            }}>
                            <EyeIcon className="w-4 h-4" />
                            Preview ({lang.toUpperCase()})
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  File Title: {languages.map((l) => file.title?.[l]).find(Boolean) || ""}
                </p>
                <p className="text-sm text-gray-600 mb-2">File Type: {file.type}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                  {languages.map((lang) => {
                    return (
                      <Input
                        key={`file-title-${lang}-${index}`}
                        className="w-full"
                        inputClassName="rounded-full bg-[#F6F5F5]"
                        labelClassName="text-sm font-normal text-black"
                        label={lang.toUpperCase() + " Title"}
                        placeholder={lang}
                        value={file.title?.[lang] || ""}
                        onChange={(e) => {
                          updateProjectResources(
                            variant,
                            region,
                            `projectFiles.${index}.title.${lang}`,
                            e.target.value,
                            null,
                            externalProjectType
                          );
                        }}
                      />
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Input
                      className="w-full"
                      inputClassName="rounded-full bg-[#F6F5F5]"
                      labelClassName="text-sm font-normal text-black"
                      label="Background Color"
                      placeholder="Background Color"
                      value={file?.colors?.background}
                      onChange={(e) => {
                        updateProjectResources(
                          variant,
                          region,
                          `projectFiles.${index}.colors.background`,
                          e.target.value,
                          null,
                          externalProjectType
                        );
                      }}
                    />
                    <span
                      className="absolute right-2 top-[65%] -translate-y-1/2 h-8 w-8 rounded-full"
                      style={{ background: resources?.projectFiles?.[index]?.colors?.background }}
                    />
                  </div>
                  <div className="relative">
                    <Input
                      className="w-full"
                      inputClassName="rounded-full bg-[#F6F5F5]"
                      labelClassName="text-sm font-normal text-black"
                      label="Text Color"
                      placeholder="Text Color"
                      value={file?.colors?.text}
                      onChange={(e) => {
                        updateProjectResources(
                          variant,
                          region,
                          `projectFiles.${index}.colors.text`,
                          e.target.value,
                          null,
                          externalProjectType
                        );
                      }}
                    />

                    <span
                      className="absolute right-2 top-[65%] -translate-y-1/2 h-8 w-8 rounded-full"
                      style={{ background: resources?.projectFiles?.[index]?.colors?.text }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <button
            className="w-full text-center text-blue-500 border border-blue-500 hover:bg-blue-500 hover:text-white rounded-md px-2 py-1"
            onClick={() => {
              updateProjectResources(
                variant,
                region,
                "projectFiles",
                [
                  ...(resources?.projectFiles || []),
                  {
                    url: {},
                    type: "",
                    title: {},
                    colors: { text: "", background: "" }
                  }
                ],
                null,
                externalProjectType
              );
            }}>
            Add File
          </button>
        </div>

        {/* Vimeo and PDF Download */}
        <div className="mb-6">
          <p className="font-semibold text-gray-700 mb-2">Vimeo and PDF Download</p>

          <div className="mb-4 flex items-center justify-between">
            <p className="font-semibold text-gray-700">Download automated VIP PDF</p>
            <ToggleComponent
              element="vimeoAndPdfDownload.studentVipPdfDownload"
              isToggle={resources?.vimeoAndPdfDownload?.studentVipPdfDownload}
              handleCompToggle={(element, value) => {
                updateProjectResources(variant, region, element, value, null, externalProjectType);
              }}
            />
          </div>

          {/* Vimeo Link Input - per language */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-black mb-2">Vimeo Links</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {languages.map((lang) => (
                <Input
                  key={lang}
                  className="w-full"
                  inputClassName="rounded-full bg-[#F6F5F5]"
                  labelClassName="text-sm font-normal text-black"
                  label={`Vimeo Link (${lang.toUpperCase()})`}
                  placeholder={`Paste Vimeo URL for ${lang.toUpperCase()}...`}
                  value={resources?.vimeoAndPdfDownload?.vimeoLink?.[lang] || ""}
                  onChange={(e) =>
                    updateProjectResources(
                      variant,
                      region,
                      `vimeoAndPdfDownload.vimeoLink.${lang}`,
                      e.target.value,
                      null,
                      externalProjectType
                    )
                  }
                />
              ))}
            </div>
          </div>

          {/* PDF Upload - per language */}
          <div className="mb-3">
            <p className="text-sm font-semibold text-black mb-2">PDF Files</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {languages.map((lang) => (
                <div key={lang} className="border border-gray-200 rounded-md p-3">
                  <label className="text-sm font-medium text-gray-600 mb-2 uppercase block">
                    {lang}
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handlePdfUpload(e.target.files[0], lang)}
                    className="cursor-pointer block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    disabled={pdfUploading[lang]}
                  />
                  {pdfUploading[lang] && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-green-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${pdfUploadProgress[lang] * 100 || 0}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Uploading: {Math.round(pdfUploadProgress[lang] * 100 || 0)}%
                      </p>
                    </div>
                  )}
                  {resources?.vimeoAndPdfDownload?.pdfLink?.[lang] && (
                    <div className="mt-3 flex items-center gap-3">
                      <a
                        href={
                          process.env.REACT_APP_AWS_URL +
                          resources.vimeoAndPdfDownload.pdfLink[lang]
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-700 bg-green-50 hover:bg-green-100 px-3 py-2 rounded-md flex items-center gap-2 text-sm">
                        <EyeIcon className="w-4 h-4" />
                        View PDF
                      </a>
                      <button
                        onClick={() =>
                          updateProjectResources(
                            variant,
                            region,
                            `vimeoAndPdfDownload.pdfLink.${lang}`,
                            "",
                            null,
                            externalProjectType
                          )
                        }
                        className="text-red-500 text-sm hover:underline">
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CustomVIPConfigThemeSetup = ({ savedValues, setValues, storeVipConfigTheme }) => {
  const fontOptions = [
    { value: "quicksand", label: "Quicksand" },
    { value: "openSans", label: "Open Sans" },
    { value: "euclid", label: "Euclid" },
    { value: "wskf", label: "WSKF" },
    { value: "arial", label: "Arial" },
    { value: "ubuntu", label: "Ubuntu" },
    { value: "satoshi", label: "Satoshi" }
  ];

  const themeStructure = {
    colorTheme: {
      header: "#000000",
      headerBorder: "#000000",
      footer: "#000000",
      footerBorder: "#000000",
      heading: "#000000",
      paragraph: "#000000",
      fillButton: {
        background: "#000000",
        text: "#000000",
        border: "#000000",
        borderWidth: "1px",
        borderRadius: "0px",
        dropShadow: "none"
      },
      outlineButton: {
        border: "#000000",
        text: "#000000",
        borderWidth: "1px",
        borderRadius: "0px",
        dropShadow: "none"
      },
      textBoxColor: "#000000",
      textBoxIconColor: "#000000",
      loader: {
        iconBackground: "#000000",
        icon: "#000000"
      }
    },
    logos: {
      header: { primary: "", secondary: "" }
    },
    fontFamily: fontOptions[0].value,
    loaderText: "Loading..."
  };

  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="bg-[#F6F6F6] rounded-md p-4">
        <p className="font-semibold text-lg">Color Theme</p>
        <div className="flex w-full flex-col gap-2 mt-2 text-right text-lg capitalize">
          {themeStructure?.colorTheme &&
            Object.keys(themeStructure?.colorTheme).length > 0 &&
            Object.keys(themeStructure?.colorTheme).map((colorId) => {
              const children = themeStructure?.colorTheme[colorId];

              if (typeof children === "object") {
                return (
                  <>
                    {Object.keys(children).map((childColor) => {
                      const isTextField =
                        childColor === "borderWidth" ||
                        childColor === "borderRadius" ||
                        childColor === "dropShadow";

                      return (
                        <div
                          key={colorId + "-" + childColor}
                          className="grid grid-cols-2 gap-2 items-center rounded-lg border border-gray-400 p-4">
                          <p>
                            {colorId}-{childColor}
                          </p>
                          {isTextField ? (
                            <Input
                              className={"w-full"}
                              inputClassName={"rounded-full border-none bg-[#fff]"}
                              placeholder={children[childColor]}
                              value={
                                savedValues?.colorTheme?.[colorId]?.[childColor] ??
                                children[childColor]
                              }
                              onChange={(e) => {
                                setValues({
                                  ...savedValues,
                                  colorTheme: {
                                    ...savedValues?.colorTheme,
                                    [colorId]: {
                                      ...savedValues?.colorTheme?.[colorId],
                                      [childColor]: e.target.value
                                    }
                                  }
                                });
                              }}
                            />
                          ) : (
                            <ColorPicker
                              width="w-full"
                              height="h-10"
                              color={savedValues?.colorTheme?.[colorId]?.[childColor]}
                              handleChange={(color) => {
                                setValues({
                                  ...savedValues,
                                  colorTheme: {
                                    ...savedValues?.colorTheme,
                                    [colorId]: {
                                      ...savedValues?.colorTheme[colorId],
                                      [childColor]: convertRGBAObjectToString(color)
                                    }
                                  }
                                });
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              } else
                return (
                  <div
                    key={colorId}
                    className="grid grid-cols-2 gap-2 items-center rounded-lg border border-gray-400 p-4">
                    <p>{colorId}</p>
                    <ColorPicker
                      width="w-full"
                      height="h-10"
                      color={savedValues?.colorTheme?.[colorId]}
                      handleChange={(color) => {
                        setValues({
                          ...savedValues,
                          colorTheme: {
                            ...savedValues?.colorTheme,
                            [colorId]: convertRGBAObjectToString(color)
                          }
                        });
                      }}
                    />
                  </div>
                );
            })}
        </div>
      </div>

      <div className="bg-[#F6F6F6] rounded-md p-4">
        <p className="font-semibold text-lg">Font Family</p>
        <div className="flex w-full flex-col gap-2 mt-2 text-lg capitalize">
          <InputSelect
            className={"w-full"}
            inputClassName={"rounded-full border-none bg-[#fff]"}
            labelClassName={"text-sm font-normal text-[#606060]"}
            // label={"Font Family"}
            placeholder={"Select Font Family"}
            value={fontOptions.find((font) => font.value === savedValues?.fontFamily)}
            options={fontOptions}
            onChange={(value) => {
              console.log(value);
              setValues({
                ...savedValues,
                fontFamily: value.value
              });
            }}
          />
        </div>
      </div>

      <div className="bg-[#F6F6F6] rounded-md p-4">
        <p className="font-semibold text-lg">Loader Text</p>
        <div className="flex w-full flex-col gap-2 mt-2 text-lg capitalize">
          <Input
            className={"w-full"}
            inputClassName={"rounded-full border-none bg-[#fff]"}
            labelClassName={"text-sm font-normal text-[#606060]"}
            // label={"Loader Text"}
            placeholder={"Loading..."}
            value={savedValues?.loaderText}
            onChange={(e) => {
              setValues({
                ...savedValues,
                loaderText: e.target.value
              });
            }}
          />
        </div>
      </div>

      <div className="bg-[#F6F6F6] rounded-md p-4">
        <p className="font-semibold text-lg">Logos</p>
        <div className="flex w-full flex-col gap-2 mt-2 text-lg capitalize">
          {themeStructure?.logos &&
            Object.keys(themeStructure?.logos).length > 0 &&
            Object.keys(themeStructure?.logos).map((logoId) => {
              const children = themeStructure?.logos[logoId];

              if (typeof children === "object") {
                return (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(children).map((childLogo) => {
                      return (
                        <div className="grid grid-cols-1 gap-2">
                          <ImageUploader
                            label={logoId + "-" + childLogo}
                            image={savedValues?.logos?.[logoId]?.[childLogo]}
                            setImage={(image) => {
                              setValues({
                                ...savedValues,
                                logos: {
                                  ...savedValues?.logos,
                                  [logoId]: { ...savedValues?.logos?.[logoId], [childLogo]: image }
                                }
                              });
                            }}
                            uploadProgress={uploadProgress?.[logoId + "-" + childLogo] || 0}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              } else {
                return (
                  <div className="grid grid-cols-1 gap-2">
                    <ImageUploader
                      label={logoId}
                      image={savedValues?.logos?.[logoId]}
                      setImage={(image) => {
                        setValues({
                          ...savedValues,
                          logos: { ...savedValues?.logos, [logoId]: image }
                        });
                      }}
                      uploadProgress={uploadProgress?.[logoId] || 0}
                    />
                  </div>
                );
              }
            })}
        </div>
      </div>

      <div className="flex justify-end">
        <Button.AdminPanelButton
          type="button"
          isSave={true}
          loading={saving}
          bgColor="#515151"
          textColor="#FFFFFF"
          color="white"
          extraClasses="hover:scale-105 transition-all"
          onClick={async () => {
            setSaving(true);
            await storeVipConfigTheme(savedValues, uploadProgress, setUploadProgress);
            setSaving(false);
          }}>
          Save
        </Button.AdminPanelButton>
      </div>
    </div>
  );
};

const defaultVIPConfigPageComponents = {
  heading: { en: "", de: "", fr: "", it: "" },
  subHeading: { en: "", de: "", fr: "", it: "" },
  studentDetails: {
    showName: false,
    showImage: false,
    nameColor: "",
    imageBorderColor: "",
    imageBackgroundColor: ""
  },
  image: {
    // default image (used when no language override provided)
    url: "",
    align: "center",
    width: "100%",
    mobileWidth: "100%",
    // per-language overrides (if provided, will override default for that lang)
    locales: {
      en: { url: "", align: "center", width: "", mobileWidth: "" },
      de: { url: "", align: "center", width: "", mobileWidth: "" },
      fr: { url: "", align: "center", width: "", mobileWidth: "" },
      it: { url: "", align: "center", width: "", mobileWidth: "" }
    }
  },
  stepBox: {
    numberTitle: {
      en: "",
      de: "",
      fr: "",
      it: "",
      background: "",
      textColor: "",
      borderColor: "",
      rounded: "2px"
    },
    title: { en: "", de: "", fr: "", it: "", textColor: "" },
    subTitle: { en: "", de: "", fr: "", it: "", textColor: "" },
    downArrow: { enabled: true, color: "" },
    background: "",
    rounded: "12px"
  },
  button: { en: "", de: "", fr: "", it: "", link: "" },
  video: {
    url: { en: "", de: "", fr: "", it: "" },
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  communicationPreferences: {
    maxASelections: 0,
    maxBSelections: 0,
    submitButton: { en: "", de: "", fr: "", it: "" },
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  mapComponent: {
    sf_icon: "",
    st_icon: "",
    nf_icon: "",
    nt_icon: "",
    sf_map_img: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    st_map_img: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    nf_map_img: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    nt_map_img: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    sf_textBoxHtml: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    st_textBoxHtml: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    nf_textBoxHtml: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    nt_textBoxHtml: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    selection_heading: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    selection_subHeading: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    option_a_text: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    option_b_text: {
      en: "",
      de: "",
      fr: "",
      it: ""
    },
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  strengthsSelection: {
    enabled: true,
    showDescriptionIcon: false,
    iconImage: "",
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  rolesSelection: {
    enabled: true,
    showDescriptionIcon: false,
    iconImage: "",
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  regionalEventSelection: {
    profileTypeId: "",
    required: false,
    errorMessage: { en: "", de: "", fr: "", it: "" }
  },
  pageSubmitButton: { en: "", de: "", fr: "", it: "" },
  infoPopup: {
    heading: { en: "", de: "", fr: "", it: "" },
    subHeading: { en: "", de: "", fr: "", it: "" },
    headingColor: "",
    subHeadingColor: "",
    backgroundColor: "",
    closeIconBackgroundColor: "",
    closeIconColor: "",
    buttonText: { en: "", de: "", fr: "", it: "" },
    buttonBackgroundColor: "",
    buttonTextColor: "",
    autoShowPopup: false
  }
};

const CustomVIPConfigPageComponentsPicker = ({
  externalProjectType,
  variant,
  region,
  pageId,
  stepPageIndex,
  addVipConfigPageComponent,
  configKey = "vipConfig"
}) => {
  const componentTypes = Object.keys(defaultVIPConfigPageComponents);

  return (
    <div className="p-3 rounded-md bg-gray-200 w-full relative">
      <p className="font-bold text-lg text-center mt-2 mb-4">Add Component</p>
      <InputSelect
        name="componentType"
        className={"mb-0"}
        options={componentTypes.map((componentType) => ({
          value: componentType,
          label: componentType
        }))}
        onChange={(e) =>
          addVipConfigPageComponent(
            variant,
            region,
            pageId,
            {
              ..._.cloneDeep(defaultVIPConfigPageComponents[e.value]),
              id: uuidv4(),
              type: e.value
            },
            stepPageIndex,
            externalProjectType,
            configKey
          )
        }
      />
    </div>
  );
};

const VIPConfigPageComponent = ({
  org,
  externalProjectType,
  component,
  componentIndex,
  variant,
  region,
  pageId,
  stepPageIndex,
  updateKeyValueInVipConfigPageComponent,
  deleteVipConfigPageComponent,
  configKey = "vipConfig"
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [regionalEventsProfileTypes, setRegionalEventsProfileTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(0);

  useEffect(() => {
    if (component.type === "regionalEventSelection") {
      (async () => {
        try {
          setLoading(true);
          const { data } = await getRegionalEvents(org?._id);
          const profileTypeOptions = [];
          data?.forEach((event) => {
            if (
              event?.profileTypeId &&
              !profileTypeOptions.some((option) => option.value === event?.profileTypeId?._id)
            ) {
              profileTypeOptions.push({
                value: event?.profileTypeId?._id,
                label: event?.profileTypeId?.name
              });
            }
          });
          setRegionalEventsProfileTypes(profileTypeOptions);
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      })();
    }
  }, []);

  const handleUpdateValue = (keyPath, value) => {
    const temp = _.cloneDeep(component);
    _.set(temp, keyPath, value);

    updateKeyValueInVipConfigPageComponent(
      variant,
      region,
      pageId,
      componentIndex,
      temp,
      stepPageIndex,
      externalProjectType,
      configKey
    );
  };

  const handleImageUpload = async (keyPath, image) => {
    try {
      const imageUrl = await uploadFileIfNeeded(image, "vipConfigPageComponents", (progress) => {
        setImageUploading({ ...imageUploading, [keyPath]: progress?.progress });
      });
      console.log("imageUrl", imageUrl);
      setImageUploading({ ...imageUploading, [keyPath]: 0 });
      handleUpdateValue(keyPath, imageUrl);
    } catch (error) {
      console.log(error);
    }
  };

  const renderMultilingualInputs = (label, keyPath, value = {}) => (
    <>
      <p className="font-bold">{label}</p>
      <div className="flex flex-row gap-1 my-2">
        {["en", "de", "fr", "it"].map((lang) => (
          <Input
            key={`${keyPath}-${lang}`}
            className="w-full"
            inputClassName="rounded-full border-none bg-[#F6F5F5]"
            labelClassName="text-sm font-normal text-black"
            label={lang.toUpperCase() + " Text"}
            placeholder={lang}
            value={value?.[lang] || ""}
            onChange={(e) =>
              handleUpdateValue(`${keyPath ? keyPath + "." : ""}${lang}`, e.target.value)
            }
          />
        ))}
      </div>
    </>
  );

  const renderColorInput = (label, keyPath, value = "") => (
    <div className="w-full mt-2">
      <p className="font-bold">{label}</p>
      <ColorPicker
        color={value}
        handleChange={(color) => handleUpdateValue(keyPath, convertRGBAObjectToString(color))}
        width="w-20"
        height="h-10"
      />
    </div>
  );

  const renderTextInput = (label, keyPath, value = "", placeholder = "") => (
    <div className="w-full mt-2">
      <p className="font-bold">{label}</p>
      <Input
        className="w-full"
        inputClassName="rounded-full border-none bg-[#F6F5F5]"
        placeholder={placeholder}
        value={value}
        onChange={(e) => handleUpdateValue(keyPath, e.target.value)}
      />
    </div>
  );

  const renderNumberInput = (label, keyPath, value = 1, placeholder = "", min = 1) => (
    <div className="w-full mt-2">
      <p className="font-bold">{label}</p>
      <Input
        className="w-full"
        inputClassName="rounded-full border-none bg-[#F6F5F5]"
        placeholder={placeholder}
        type="number"
        min={min}
        value={value}
        onChange={(e) => handleUpdateValue(keyPath, Math.max(min, Number(e.target.value) || min))}
      />
    </div>
  );

  const renderSelectInput = (label, keyPath, value = "", options = []) => (
    <div className="w-full mt-2">
      <p className="font-bold">{label}</p>
      <InputSelect
        name={`${keyPath}-${componentIndex}`}
        className="mb-0"
        options={options.map((option) => ({
          value: option?.value || option,
          label: option?.label || option
        }))}
        value={options.find((option) => option?.value === value) || value}
        onChange={(e) => handleUpdateValue(keyPath, e.value)}
      />
    </div>
  );

  const renderToggleInput = (label, keyPath, value = false) => (
    <div className="flex items-center justify-between my-2">
      <p className="font-bold">{label}</p>
      <ToggleComponent
        handleCompToggle={() => handleUpdateValue(keyPath, !value)}
        isToggle={value}
      />
    </div>
  );

  const normalizeImageValue = (image) => {
    if (image && typeof image === "object" && !image.type && Object.keys(image).length === 0) {
      return "";
    }
    return image;
  };

  const renderImageUploader = (label, keyPath, value = "") => (
    <div className="w-full mt-2">
      <ImageUploader
        image={value}
        setImage={(image) => handleUpdateValue(keyPath, normalizeImageValue(image))}
        label={label}
        uploadProgress={imageUploading?.[keyPath] || 0}
      />
      {value?.type && !imageUploading?.[keyPath] && (
        <button
          onClick={() => handleImageUpload(keyPath, value)}
          className="text-lg bg-corange-100 text-white p-2 rounded-md">
          Confirm Upload this image
        </button>
      )}
    </div>
  );

  const renderComponentInputs = () => {
    switch (component.type) {
      case "heading":
        return (
          <>
            {renderMultilingualInputs("Heading Text", "", component)}
            {renderColorInput("Heading Color", "color", component.color)}
          </>
        );

      case "subHeading":
        return (
          <>
            {renderMultilingualInputs("Sub Heading Text", "", component)}
            {renderColorInput("Sub Heading Color", "color", component.color)}
          </>
        );

      case "studentDetails":
        return (
          <>
            {renderToggleInput("Show Name", "showName", component.showName)}
            {renderColorInput("Name Color", "nameColor", component.nameColor)}
            {renderToggleInput("Show Image", "showImage", component.showImage)}
            {renderColorInput("Image Border Color", "imageBorderColor", component.imageBorderColor)}
            {renderColorInput(
              "Image Background Color",
              "imageBackgroundColor",
              component.imageBackgroundColor
            )}
          </>
        );

      case "image":
        return (
          <>
            {/* Default (fallback) image settings */}
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Default Image</p>
              {renderImageUploader("Upload Image", "url", component.url)}
              {renderSelectInput("Alignment", "align", component.align, [
                { value: "left", label: "left" },
                { value: "center", label: "center" },
                { value: "right", label: "right" }
              ])}
              {renderTextInput(
                "Width (Percent or rem or px or any other css unit)",
                "width",
                component.width,
                "Enter width percentage or rem or px or any other css unit"
              )}
              {renderTextInput(
                "Mobile Width (Percent or rem or px or any other css unit)",
                "mobileWidth",
                component.mobileWidth,
                "Enter width percentage or rem or px or any other css unit"
              )}
            </div>

            {/* Per-language overrides */}
            <div className="border border-gray-300 p-3 rounded-md mb-1">
              <p className="font-bold text-lg mb-3">Localized Overrides</p>
              {(org?.languages?.length ? org.languages : Object.keys(component?.locales)).map(
                (lang) => (
                  <div
                    key={`image-locale-${lang}`}
                    className="border border-gray-200 p-3 rounded-md mb-3">
                    <p className="font-bold mb-2">{lang.toUpperCase()}</p>
                    {renderImageUploader(
                      `Upload Image (${lang.toUpperCase()})`,
                      `locales.${lang}.url`,
                      component?.locales?.[lang]?.url
                    )}
                    {renderSelectInput(
                      `Alignment (${lang.toUpperCase()})`,
                      `locales.${lang}.align`,
                      component?.locales?.[lang]?.align,
                      [
                        { value: "left", label: "left" },
                        { value: "center", label: "center" },
                        { value: "right", label: "right" }
                      ]
                    )}
                    {renderTextInput(
                      `Width (${lang.toUpperCase()})`,
                      `locales.${lang}.width`,
                      component?.locales?.[lang]?.width,
                      ""
                    )}
                    {renderTextInput(
                      `Mobile Width (${lang.toUpperCase()})`,
                      `locales.${lang}.mobileWidth`,
                      component?.locales?.[lang]?.mobileWidth,
                      ""
                    )}
                  </div>
                )
              )}
            </div>
          </>
        );

      case "stepBox":
        return (
          <>
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Number Title</p>
              {renderMultilingualInputs("Number Title Text", "numberTitle", component.numberTitle)}
              <div className="grid grid-cols-3 gap-2 text-center">
                {renderColorInput(
                  "Background Color",
                  "numberTitle.background",
                  component.numberTitle?.background
                )}
                {renderColorInput(
                  "Text Color",
                  "numberTitle.textColor",
                  component.numberTitle?.textColor
                )}
                {renderColorInput(
                  "Border Color",
                  "numberTitle.borderColor",
                  component.numberTitle?.borderColor
                )}
              </div>
              {renderTextInput(
                "Border Radius",
                "numberTitle.borderRadius",
                component.numberTitle?.borderRadius,
                "e.g., 2px"
              )}
            </div>

            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Title</p>
              {renderMultilingualInputs("Title Text", "title", component.title)}
              {renderColorInput("Text Color", "title.textColor", component.title?.textColor)}
            </div>

            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Sub Title</p>
              {renderMultilingualInputs("Sub Title Text", "subTitle", component.subTitle)}
              {renderColorInput("Text Color", "subTitle.textColor", component.subTitle?.textColor)}
            </div>

            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Down Arrow</p>
              {renderToggleInput(
                "Enable Down Arrow",
                "downArrow.enabled",
                component.downArrow?.enabled
              )}
              {renderColorInput("Arrow Color", "downArrow.color", component.downArrow?.color)}
            </div>

            {renderColorInput("Background Color", "background", component.background)}
            {renderTextInput("Border Radius", "borderRadius", component.borderRadius, "e.g., 12px")}
          </>
        );

      case "button":
        return (
          <>
            {renderMultilingualInputs("Button Text", "", component)}
            {renderTextInput("Button Link", "link", component.link, "Enter URL or route")}
          </>
        );

      case "video":
        return (
          <>
            {renderMultilingualInputs("Video URL (Enter Vimeo ID only)", "url", component.url)}
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "communicationPreferences":
        return (
          <>
            {renderNumberInput(
              "Max A Selections",
              "maxASelections",
              component.maxASelections,
              "Enter minimum 0",
              0
            )}
            {renderNumberInput(
              "Max B Selections",
              "maxBSelections",
              component.maxBSelections,
              "Enter minimum 0",
              0
            )}
            {renderMultilingualInputs("Submit Button Text", "submitButton", component.submitButton)}
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "strengthsSelection":
        return (
          <>
            {renderToggleInput("Enable Strengths Selection", "enabled", component.enabled)}
            {renderToggleInput(
              "Show Description Icon",
              "showDescriptionIcon",
              component.showDescriptionIcon
            )}
            {renderImageUploader("Icon Image", "iconImage", component.iconImage)}
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "rolesSelection":
        return (
          <>
            {renderToggleInput("Enable Roles Selection", "enabled", component.enabled)}
            {renderToggleInput(
              "Show Description Icon",
              "showDescriptionIcon",
              component.showDescriptionIcon
            )}
            {renderImageUploader("Icon Image", "iconImage", component.iconImage)}
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "regionalEventSelection":
        return (
          <>
            {renderSelectInput(
              "Profile Type ID",
              "profileTypeId",
              component.profileTypeId,
              regionalEventsProfileTypes
            )}
            {renderToggleInput("Required", "required", component.required)}
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "mapComponent":
        return (
          <>
            {/* Icon uploads */}
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Icons</p>
              <div className="grid grid-cols-2 gap-4">
                {renderImageUploader("SF Icon", "sf_icon", component.sf_icon)}
                {renderImageUploader("ST Icon", "st_icon", component.st_icon)}
                {renderImageUploader("NF Icon", "nf_icon", component.nf_icon)}
                {renderImageUploader("NT Icon", "nt_icon", component.nt_icon)}
              </div>
            </div>

            {/* Map images */}
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Map Images</p>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="font-semibold mb-2">SF Map Images</p>
                  <div className="grid grid-cols-2 gap-2">
                    {renderImageUploader("SF EN", "sf_map_img.en", component.sf_map_img?.en)}
                    {renderImageUploader("SF DE", "sf_map_img.de", component.sf_map_img?.de)}
                    {renderImageUploader("SF FR", "sf_map_img.fr", component.sf_map_img?.fr)}
                    {renderImageUploader("SF IT", "sf_map_img.it", component.sf_map_img?.it)}
                  </div>
                </div>
                <div>
                  <p className="font-semibold mb-2">ST Map Images</p>
                  <div className="grid grid-cols-2 gap-2">
                    {renderImageUploader("ST EN", "st_map_img.en", component.st_map_img?.en)}
                    {renderImageUploader("ST DE", "st_map_img.de", component.st_map_img?.de)}
                    {renderImageUploader("ST FR", "st_map_img.fr", component.st_map_img?.fr)}
                    {renderImageUploader("ST IT", "st_map_img.it", component.st_map_img?.it)}
                  </div>
                </div>
                <div>
                  <p className="font-semibold mb-2">NF Map Images</p>
                  <div className="grid grid-cols-2 gap-2">
                    {renderImageUploader("NF EN", "nf_map_img.en", component.nf_map_img?.en)}
                    {renderImageUploader("NF DE", "nf_map_img.de", component.nf_map_img?.de)}
                    {renderImageUploader("NF FR", "nf_map_img.fr", component.nf_map_img?.fr)}
                    {renderImageUploader("NF IT", "nf_map_img.it", component.nf_map_img?.it)}
                  </div>
                </div>
                <div>
                  <p className="font-semibold mb-2">NT Map Images</p>
                  <div className="grid grid-cols-2 gap-2">
                    {renderImageUploader("NT EN", "nt_map_img.en", component.nt_map_img?.en)}
                    {renderImageUploader("NT DE", "nt_map_img.de", component.nt_map_img?.de)}
                    {renderImageUploader("NT FR", "nt_map_img.fr", component.nt_map_img?.fr)}
                    {renderImageUploader("NT IT", "nt_map_img.it", component.nt_map_img?.it)}
                  </div>
                </div>
              </div>
            </div>

            {/* Text Box HTML */}
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Text Box HTML</p>
              <div className="mb-4">
                <p className="font-semibold mb-2">SF Text Box HTML</p>
                {renderMultilingualInputs(
                  "SF Text Box HTML",
                  "sf_textBoxHtml",
                  component.sf_textBoxHtml
                )}
              </div>
              <div className="mb-4">
                <p className="font-semibold mb-2">ST Text Box HTML</p>
                {renderMultilingualInputs(
                  "ST Text Box HTML",
                  "st_textBoxHtml",
                  component.st_textBoxHtml
                )}
              </div>
              <div className="mb-4">
                <p className="font-semibold mb-2">NF Text Box HTML</p>
                {renderMultilingualInputs(
                  "NF Text Box HTML",
                  "nf_textBoxHtml",
                  component.nf_textBoxHtml
                )}
              </div>
              <div className="mb-4">
                <p className="font-semibold mb-2">NT Text Box HTML</p>
                {renderMultilingualInputs(
                  "NT Text Box HTML",
                  "nt_textBoxHtml",
                  component.nt_textBoxHtml
                )}
              </div>
            </div>

            {/* Selection texts */}
            <div className="border border-gray-300 p-3 rounded-md mb-4">
              <p className="font-bold text-lg mb-3">Selection Texts</p>
              <div className="mb-4">
                {renderMultilingualInputs(
                  "Selection Heading",
                  "selection_heading",
                  component.selection_heading
                )}
              </div>
              <div className="mb-4">
                {renderMultilingualInputs(
                  "Selection Sub Heading",
                  "selection_subHeading",
                  component.selection_subHeading
                )}
              </div>
              <div className="mb-4">
                {renderMultilingualInputs(
                  "Option A Text",
                  "option_a_text",
                  component.option_a_text
                )}
              </div>
              <div className="mb-4">
                {renderMultilingualInputs(
                  "Option B Text",
                  "option_b_text",
                  component.option_b_text
                )}
              </div>
            </div>
            {renderMultilingualInputs("Error Message", "errorMessage", component.errorMessage)}
          </>
        );

      case "pageSubmitButton":
        return renderMultilingualInputs("Submit Button Text", "", component);

      case "infoPopup":
        return (
          <>
            {renderMultilingualInputs("Heading", "heading", component.heading)}
            {renderMultilingualInputs("Sub Heading", "subHeading", component.subHeading)}
            {renderColorInput("Heading Color", "headingColor", component.headingColor)}
            {renderColorInput("Sub Heading Color", "subHeadingColor", component.subHeadingColor)}
            {renderColorInput("Background Color", "backgroundColor", component.backgroundColor)}
            {renderColorInput(
              "Close Icon Background Color",
              "closeIconBackgroundColor",
              component.closeIconBackgroundColor
            )}
            {renderColorInput("Close Icon Color", "closeIconColor", component.closeIconColor)}
            {renderMultilingualInputs("Button Text", "buttonText", component.buttonText)}
            {renderColorInput(
              "Button Background Color",
              "buttonBackgroundColor",
              component.buttonBackgroundColor
            )}
            {renderColorInput("Button Text Color", "buttonTextColor", component.buttonTextColor)}
            {renderToggleInput("Auto Show Popup", "autoShowPopup", component.autoShowPopup)}
          </>
        );

      default:
        return <p className="text-red-500">Unknown component type: {component.type}</p>;
    }
  };

  return (
    <div className="p-3 rounded-md bg-gray-200 w-full relative my-2">
      <span className="block w-5 h-5 absolute right-3 top-3 cursor-pointer text-red-600">
        <TrashIcon
          onClick={() =>
            deleteVipConfigPageComponent(
              variant,
              region,
              pageId,
              componentIndex,
              stepPageIndex,
              externalProjectType,
              configKey
            )
          }
        />
      </span>

      <p
        className="font-bold text-base text-center mt-2 mb-4 cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}>
        <span className="inline-block px-2 py-1 rounded text-center bg-blue-100 text-black mb-2">
          {pageId}
          {stepPageIndex !== null ? `-${stepPageIndex + 1}` : ""} Component #{componentIndex + 1}
        </span>
        <br />
        {component.type.charAt(0).toUpperCase() + component.type.slice(1)} Component
      </p>

      {!collapsed && <div className="mt-4">{renderComponentInputs()}</div>}

      {loading && (
        <div className="absolute top-0 left-0 w-full h-full bg-black/10 backdrop-blur-sm z-10 flex justify-center items-center">
          <ClipLoader color="#000" />
        </div>
      )}
    </div>
  );
};

const CustomVIPConfigPageComponentsSetup = ({
  org,
  externalProjectType,
  pageId,
  variant,
  region,
  stepPageIndex,
  components,
  configKey = "vipConfig",
  addVipConfigPageComponent,
  deleteVipConfigPageComponent,
  onDragEndVipConfigPageComponent,
  updateKeyValueInVipConfigPageComponent
}) => {
  return (
    <div className="border border-[#606060] p-2 rounded-md">
      <DragDropContext
        onDragEnd={(result) => {
          onDragEndVipConfigPageComponent(
            variant,
            region,
            pageId,
            stepPageIndex,
            result,
            externalProjectType,
            configKey
          );
        }}>
        <Droppable
          droppableId={`vipConfigPage-${variant}-${region}-${pageId}-${stepPageIndex}-components`}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {components?.map((component, componentIndex) => (
                <Draggable
                  key={`vipConfigPage-${variant}-${region}-${pageId}-${stepPageIndex}-${component.id}-${componentIndex}`}
                  draggableId={`vipConfigPage-${variant}-${region}-${pageId}-${stepPageIndex}-${component.id}-${componentIndex}`}
                  index={componentIndex}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      id={`vipConfigPage-${variant}-${region}-${pageId}-${stepPageIndex}-${component.id}-${componentIndex}`}>
                      <VIPConfigPageComponent
                        org={org}
                        externalProjectType={externalProjectType}
                        component={component}
                        componentIndex={componentIndex}
                        variant={variant}
                        region={region}
                        pageId={pageId}
                        stepPageIndex={stepPageIndex}
                        configKey={configKey}
                        updateKeyValueInVipConfigPageComponent={
                          updateKeyValueInVipConfigPageComponent
                        }
                        deleteVipConfigPageComponent={deleteVipConfigPageComponent}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <CustomVIPConfigPageComponentsPicker
        externalProjectType={externalProjectType}
        variant={variant}
        region={region}
        pageId={pageId}
        stepPageIndex={stepPageIndex}
        configKey={configKey}
        addVipConfigPageComponent={addVipConfigPageComponent}
      />
    </div>
  );
};
